import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Instagram, CheckCircle, ArrowUp, ArrowDown, RefreshCw, Users, Settings, Plus, X, Heart, UserPlus, UserMinus, MessageCircle, Eye, Play, Link, RotateCw, Lock, List, Cast, Shield, ShieldOff, Trash2, Sparkles, ChevronDown, Hash, Laptop, Monitor, Server, Search, Film, Target } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import SharedPageHeader from '../components/layout/PageHeader';
import { getProfilePicUrl, getLocalProfilePicUrl } from '../config/imageUrls';

const PageContainer = styled.div`
  padding: 20px;
`;



const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: var(--card-bg);
  border: 2px solid var(--card-border);
  border-radius: 10px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, var(--surface-hover), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: var(--primary);
    color: var(--text-primary);
    box-shadow: var(--shadow-hover);
  }

  &:hover::before {
    left: 100%;
  }
`;

const WorkflowBadge = styled.span`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  transition: all 0.3s ease;
  display: inline-block;
  background: ${props => {
    if (props.$workflow === 'A') return 'linear-gradient(135deg, var(--info), var(--info))';
    if (props.$workflow === 'B') return 'linear-gradient(135deg, var(--success), var(--success))';
    if (props.$workflow === 'C') return 'linear-gradient(135deg, var(--warning), var(--warning))';
    if (props.$workflow === 'D') return 'linear-gradient(135deg, var(--error), var(--error))';
    if (props.$workflow === 'FULL') return 'linear-gradient(135deg, var(--primary), var(--secondary))';
    return 'linear-gradient(135deg, var(--text-muted), var(--text-muted))';
  }};
  color: #fff;
  box-shadow: ${props => {
    if (props.$workflow === 'A') return '0 4px 12px rgba(59, 130, 246, 0.4)';
    if (props.$workflow === 'B') return '0 4px 12px rgba(16, 185, 129, 0.4)';
    if (props.$workflow === 'C') return '0 4px 12px rgba(245, 158, 11, 0.4)';
    if (props.$workflow === 'D') return '0 4px 12px rgba(239, 68, 68, 0.4)';
    if (props.$workflow === 'FULL') return 'var(--shadow)';
    return '0 4px 12px rgba(100, 116, 139, 0.4)';
  }};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => {
      if (props.$workflow === 'A') return '0 6px 20px rgba(59, 130, 246, 0.6)';
      if (props.$workflow === 'B') return '0 6px 20px rgba(16, 185, 129, 0.6)';
      if (props.$workflow === 'C') return '0 6px 20px rgba(245, 158, 11, 0.6)';
      if (props.$workflow === 'D') return '0 6px 20px rgba(239, 68, 68, 0.6)';
      if (props.$workflow === 'FULL') return 'var(--shadow-hover)';
      return '0 6px 20px rgba(100, 116, 139, 0.6)';
    }};
  }
`;

const AccountTypeCell = styled.span`
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  transition: all 0.3s ease;
  display: inline-block;
  background: ${props => {
    if (props.type === 'client') return 'linear-gradient(135deg, var(--primary), var(--secondary))';
    if (props.type === 'client2') return 'linear-gradient(135deg, var(--secondary), var(--accent))';
    if (props.type === 'personnel') return 'linear-gradient(135deg, var(--info), var(--info))';
    if (props.type === 'impulse') return 'linear-gradient(135deg, var(--error), var(--error))';
    if (props.type === 'old') return 'linear-gradient(135deg, var(--warning), var(--warning))';
    if (props.type === 'our') return 'linear-gradient(135deg, var(--primary), var(--primary))';
    if (props.type === 'test') return 'linear-gradient(135deg, var(--success), var(--success))';
    if (props.type === 'backup') return 'linear-gradient(135deg, var(--warning), var(--warning))';
    if (props.type === 'standard') return 'linear-gradient(135deg, var(--text-muted), var(--text-muted))';
    if (props.type === 'timo') return 'linear-gradient(135deg, var(--accent), var(--accent))';
    return 'linear-gradient(135deg, #666, #888)';
  }};
  color: #fff;

  &:hover { opacity: .85; }
`;

const SettingsButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 2px solid var(--primary);
  background: var(--surface);
  color: var(--primary);
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover { opacity: 0.85; }
`;

const CastButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 2px solid ${props => props.disabled ? 'var(--border)' : 'var(--primary)'};
  background: ${props => props.disabled 
    ? 'var(--surface)' 
    : 'var(--surface)'
  };
  color: ${props => props.disabled ? 'var(--text-muted)' : 'var(--primary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: var(--surface-hover);
    transform: translate(-50%, -50%);
    transition: width 0.4s, height 0.4s;
  }

  &:hover:not(:disabled)::before {
    width: 100px;
    height: 100px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: var(--shadow-hover);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  border: 2px solid var(--primary);
  border-radius: 10px;
  color: white;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px var(--border);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s;
  }

  &:hover {
    background: linear-gradient(135deg, var(--secondary) 0%, #9333EA 100%);
    transform: translateY(-3px);
    box-shadow: var(--shadow-hover);
    border-color: var(--secondary);
  }

  &:hover::before {
    left: 100%;
  }

  &:active {
    transform: translateY(-1px);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: rotate(90deg) scale(1.1);
  }
`;

const AssociateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: linear-gradient(135deg, var(--surface) 0%, rgba(168, 85, 247, 0.05) 100%);
  border: 2px solid var(--border);
  border-radius: 10px;
  color: var(--secondary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, var(--surface-hover), transparent);
    transition: left 0.5s;
  }

  &:hover {
    background: var(--surface-hover);
    transform: translateY(-2px);
    border-color: var(--primary);
    color: var(--text-primary);
    box-shadow: 0 4px 16px var(--border);
  }

  &:hover::before {
    left: 100%;
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: scale(1.2);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease;
  overflow-y: auto;
  padding: 20px;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: var(--card-bg);
  border-radius: 12px;
  width: 95%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-hover);
  border: 1px solid var(--card-border);
  animation: slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
  position: relative;
  max-height: 90vh;

  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to { 
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: ${props => props.theme.background};
  border-bottom: 1px solid ${props => props.theme.border};
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.theme.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  svg {
    color: ${props => props.theme.primary};
  }
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: transparent;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.theme.error};
    color: white;
    transform: scale(1.1);
  }
`;

const ModalBody = styled.div`
  padding: 0;
  background: var(--card-bg);
  overflow-y: auto;
  max-height: calc(90vh - 140px);
`;

const SettingsSections = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border-top: 1px solid var(--card-border);
`;

const SettingsSection = styled.div`
  padding: 24px;
  border-right: 1px solid var(--card-border);
  
  &:last-child {
    border-right: none;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid ${props => props.$color || 'var(--primary)'};
`;

const SectionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.$color || 'var(--primary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: ${props => props.$color ? `0 4px 12px ${props.$color}40` : 'var(--shadow)'};
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const GeneralSection = styled.div`
  padding: 24px;
  border-bottom: 1px solid var(--card-border);
  background: var(--bg);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s ease;
  font-weight: 500;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--surface);
  }

  &:hover {
    border-color: var(--primary);
  }

  &::placeholder {
    color: #6d6f78;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--surface);
  }

  &:hover {
    border-color: var(--primary);
  }

  option {
    background: var(--bg);
    color: var(--text-primary);
    padding: 12px;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  background: var(--bg);
  border-top: 1px solid var(--card-border);
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: transparent;
  border: 1px solid var(--card-border);
  border-radius: 8px;
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: var(--text-primary);
    transform: translateY(-1px);
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--secondary);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 26px 34px 24px 60px 34px 26px 34px minmax(80px,1fr) 38px 34px 38px 38px 38px 38px 38px 38px 38px 44px 58px 30px;
  gap: 0;
  padding: 0 12px;
  background: var(--surface);
  border-bottom: 2px solid var(--border);
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableHeaderCell = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 6px;
  font-size: 10px;
  font-weight: 700;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
  &:hover { color: var(--text-primary); }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 26px 34px 24px 60px 34px 26px 34px minmax(80px,1fr) 38px 34px 38px 38px 38px 38px 38px 38px 38px 44px 58px 30px;
  gap: 0;
  padding: 0 12px;
  align-items: center;
  border-bottom: 1px solid var(--card-border);
  background: ${p => p.$isRunning ? 'rgba(34,197,94,.06)' : p.$inWorkflow ? 'rgba(165,145,239,.04)' : 'var(--card-bg)'};
  opacity: ${p => p.$active === false ? 0.5 : 1};
  transition: background .15s;
  &:hover { background: var(--surface); }
`;

const Cell = styled.div`
  padding: 4px 3px;
  font-size: 12px;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  min-height: 44px;
  overflow: hidden;
  opacity: ${p => p.$active === false ? 0.4 : 1};
`;

const UsernameCell = styled(Cell)`
  gap: 6px;
  font-weight: 600;
  color: var(--primary);
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  & > span { overflow: hidden; text-overflow: ellipsis; }
`;

const DeviceBadge = styled.span`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 700;
  background: var(--surface);
  color: var(--text-secondary);
  border: 1px solid var(--border);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90px;
  display: inline-block;
`;

const StatusIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$active ? 'var(--success)' : 'var(--text-muted)'};
  box-shadow: ${p => p.$active ? '0 0 6px var(--success)' : 'none'};
`;

const ProfileImage = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--border);
  cursor: pointer;
  transition: border-color .15s;
  &:hover { border-color: var(--primary); }
`;

const ProfilePlaceholder = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--surface);
  border: 2px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-muted);
  cursor: pointer;
`;

const AccountsTable = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  overflow: hidden;
  overflow-x: auto;
`;

const Accounts = ({ onNavigateToStats, onNavigateToAutomation, onSelectDevice, setMirrorOpen, activeWorkstation }) => {
  useTheme();
  const [accounts, setAccounts] = useState([]);
  const [allInstagramAccounts, setAllInstagramAccounts] = useState([]); // TOUS les comptes pour la modale d'association
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('default');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedClientTypes, setSelectedClientTypes] = useState(() => {
    const saved = localStorage.getItem('selectedClientTypes');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedConnectionStatus, setSelectedConnectionStatus] = useState(() => {
    const saved = localStorage.getItem('selectedConnectionStatus');
    return saved ? JSON.parse(saved) : 'all'; // 'all', 'online', 'offline'
  });
  const [selectedWorkstations, setSelectedWorkstations] = useState([]);
  const [selectedRunningStatus, setSelectedRunningStatus] = useState('all'); // 'all', 'running', 'stopped'
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showAssociateModal, setShowAssociateModal] = useState(false);
  const [associateDropdownOpen, setAssociateDropdownOpen] = useState(false);
  const [runningScripts, setRunningScripts] = useState({}); // account_id -> boolean
  const [showListsModal, setShowListsModal] = useState(false);
  const [selectedListAccount] = useState(null);
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [selectedScriptAccount] = useState(null);
  const [scriptParams, setScriptParams] = useState({
    scriptType: 'interact',
    scrapeTarget: '',
    scrapeTypes: ['followers', 'following', 'feed', 'stories', 'post-likers', 'hashtag'],
    scrapeType: 'followers',
    interactType: 'followers',
    interactionType: 'followers', // Par défaut: Followers coché
    maxDuration: 60,
    unfollowMaxDuration: 30,
    specificTarget: '',
    unfollowCount: 50,
    unfollowMethod: 'FIFO',
    unfollowType: 'database-global-search',
    geminiAnalysis: true,
    deepAnalysis: true,
    totalScrapeLimit: '',
    scrapeLimitPerSource: ''
  });
  const [accountLists, setAccountLists] = useState({ whitelist: null, blacklist: null, unfollow: null });
  const [accountTargets] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [selectedListType, setSelectedListType] = useState('whitelist');
  const [showGeminiPromptModal, setShowGeminiPromptModal] = useState(false);
  const [selectedGeminiAccount, setSelectedGeminiAccount] = useState(null);
  const [geminiPrompt, setGeminiPrompt] = useState('');
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [instagramPackages, setInstagramPackages] = useState([]);
  const [showLikeBar, setShowLikeBar] = useState(true);
  const [showFollowBar, setShowFollowBar] = useState(false);
  const [association, setAssociation] = useState({
    account_id: '',
    device_id: '',
    status: 'inactive',
    interact_targets: '',
    successful_interactions_limit_per_source: 150,
    likes_count: 150,
    follow_percentage: 25,
    stories_count: 500,
    total_story_limit: 500,
    total_likes_limit: 150,
    story_like_percentage: 20,
    unfollow_delay: 7,
    workflow: 'FULL',
    comment_percentage: 0,
    dm_limit: 0,
    like_target_for_the_day: 0,
    follow_target_for_the_day: 0
  });
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [devices, setDevices] = useState([]);
  const [newAccount, setNewAccount] = useState({
    username: '',
    password: '',
    account_type: 'standard'
  });

  const [deviceSettings, setDeviceSettings] = useState({
    device_id: '',
    status: 'inactive',
    interact_targets: '',
    successful_interactions_limit_per_source: '',
    likes_count: '',
    follow_percentage: 25,
    stories_count: '',
    total_story_limit: 500,
    total_likes_limit: 150,
    story_like_percentage: 20,
    session_length_in_mins_limit: '45-75',
    order_position: 5,
    unfollow_limit: 10,
    min_following_hours: 24,
    unfollow_delay: '10-16',
    comment_percentage: 0,
    dm_list: '',
    dm_new_followers: 'No',
    dm_to_bot_followed: 'No',
    dm_daily_limit: 0,
    like_target_for_the_day: 150,
    follow_target_for_the_day: 100,
    workflow: 'A',
    unfollow_type_script: '',
    unfollow_count: '',
    unfollow_older_than_days: 7,
    total_scrape_limit: '',
    scrape_limit_per_source: '',
    gemini_analysis: true,
    deep_analysis: false
  });

  useEffect(() => {
    fetchAccounts();
    fetchAllInstagramAccounts(); // Charger TOUS les comptes pour la modale
    fetchDevices();
    fetchInstagramPackages(); // Charger les packages Instagram disponibles
    fetchScriptStatuses(); // ✅ Vérifier immédiatement au montage
  }, []);

  // Sauvegarder la sélection des types de clients et statut de connexion dans localStorage
  useEffect(() => {
    localStorage.setItem('selectedClientTypes', JSON.stringify(selectedClientTypes));
    localStorage.setItem('selectedConnectionStatus', JSON.stringify(selectedConnectionStatus));
    
    // Auto-refresh toutes les 2 minutes (SANS recharger la page)
    const interval = setInterval(() => {
      fetchAccounts();
      fetchAllInstagramAccounts();
    }, 120000);
    
    // ✅ Vérifier le statut des scripts toutes les 10 secondes (plus réactif)
    const statusInterval = setInterval(() => {
      fetchScriptStatuses();
    }, 10000);
    
    return () => {
      clearInterval(interval);
      clearInterval(statusInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - run only once on mount

  const fetchAllInstagramAccounts = async () => {
    try {
      console.log('🔄 Chargement de TOUS les comptes Instagram pour la modale...');
      console.log('📡 Appel API: GET /api/instagram-accounts');
      const response = await axios.get('/api/instagram-accounts');
      console.log('📥 Réponse API reçue:', response.data);
      
      if (response.data && response.data.success) {
        console.log(`✅ ${response.data.accounts.length} comptes Instagram chargés`);
        console.log('📋 Comptes:', response.data.accounts);
        setAllInstagramAccounts(response.data.accounts);
      } else {
        console.warn('⚠️ Réponse API invalide:', response.data);
        setAllInstagramAccounts([]);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des comptes Instagram:', error);
      console.error('📊 Détails de l\'erreur:', error.response?.data || error.message);
      setAllInstagramAccounts([]);
    }
  };

  const fetchAccounts = async () => {
    try {
      console.log('Récupération des comptes Instagram...');
      // ✅ Fetch cards + all daily stats in parallel (2 requests instead of N+1)
      const [cardsResponse, batchStatsResponse] = await Promise.all([
        axios.get('/api/account-device-cards'),
        axios.get('/api/daily-stats-batch').catch(() => ({ data: {} }))
      ]);
      
      if (cardsResponse.data && Array.isArray(cardsResponse.data)) {
        const statsMap = batchStatsResponse.data || {};
        console.log(`✅ ${cardsResponse.data.length} comptes, stats batch: ${Object.keys(statsMap).length} comptes`);
        
        // Deduplicate: keep only ONE row per account (first occurrence wins — ordered by order_position)
        const seenAccountIds = new Set();
        const uniqueCards = cardsResponse.data.filter(card => {
          const accountId = card.instagram_account_id || card.account_id;
          if (!accountId || seenAccountIds.has(accountId)) return false;
          seenAccountIds.add(accountId);
          return true;
        });
        console.log(`🔄 Déduplication: ${cardsResponse.data.length} → ${uniqueCards.length} comptes uniques`);
        
        const accountsWithStats = uniqueCards.map(card => {
          const accountId = card.instagram_account_id || card.account_id;
          const s = statsMap[accountId] || {};
          
          return {
            id: accountId,
            username: card.instagramUsername,
            password: '',
            is_active: card.status === 'active',
            account_type: card.account_type || 'N/A',
            workflow_character: card.workflow || '-',
            device_id: card.device_id,
            device_name: card.device_name,
            device_connected: card.device_connected || false,
            is_restarter: card.is_restarter || false,
            workstation_id: card.workstation_id || null,
            workstation_name: card.workstation_name || null,
            app_id: card.app_id || 'com.instagram.android',
            like_target_for_the_day: parseInt(card.like_target_for_the_day) || 0,
            follow_target_for_the_day: parseInt(card.follow_target_for_the_day) || 0,
            total_profile_views: card.total_profile_views || 0,
            private_profile_views: card.private_profile_views || 0,
            today_likes: s.likes || 0,
            today_follows: s.follows || 0,
            today_unfollows: s.unfollows || 0,
            today_comments: s.comments || 0,
            today_story_likes: s.story_likes || 0,
            today_story_views: s.story_views || 0,
            today_profile_views: s.get_profiles || 0,
            today_private_profile_views: s.private_profiles || 0,
            today_unique_users: s.unique_users || 0,
            today_scraped: s.scraped || 0
          };
        });
        
        setAccounts(accountsWithStats);
      } else {
        console.error('❌ Format de réponse incorrect:', cardsResponse.data);
        throw new Error('Format de réponse incorrect');
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des comptes:', error);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchScriptStatuses = async () => {
    // Use callback to get current accounts state
    setAccounts(currentAccounts => {
      if (currentAccounts.length === 0) return currentAccounts;
      
      // Fetch statuses asynchronously with batching to avoid ERR_INSUFFICIENT_RESOURCES
      (async () => {
        try {
          console.log('🔍 Fetching script statuses for', currentAccounts.length, 'accounts');
          
          // ✅ Traiter par batch de 5 requêtes simultanées maximum
          const BATCH_SIZE = 5;
          const statusMap = {};
          
          for (let i = 0; i < currentAccounts.length; i += BATCH_SIZE) {
            const batch = currentAccounts.slice(i, i + BATCH_SIZE);
            const batchPromises = batch.map(account => 
              axios.get(`/api/script-status/${account.id}`)
                .then(res => {
                  return { account_id: account.id, running: res.data.running };
                })
                .catch(err => {
                  return { account_id: account.id, running: false };
                })
            );
            
            const batchResults = await Promise.all(batchPromises);
            batchResults.forEach(({ account_id, running }) => {
              statusMap[account_id] = running;
            });
            
            // Petit délai entre les batches pour éviter la saturation
            if (i + BATCH_SIZE < currentAccounts.length) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          }
          
          setRunningScripts(statusMap);
        } catch (error) {
          console.error('❌ Erreur lors de la vérification des statuts:', error);
        }
      })();
      
      return currentAccounts; // Return unchanged
    });
  };

  const fetchInstagramPackages = async () => {
    try {
      const response = await axios.get('/api/instagram-packages');
      if (response.data && response.data.success) {
        setInstagramPackages(response.data.packages);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des packages Instagram:', error);
    }
  };

  const handleChangeAppId = async (accountId, newAppId) => {
    try {
      const response = await axios.put('/api/instagram-packages/assign', {
        account_id: accountId,
        app_id: newAppId
      });
      if (response.data.success) {
        // Update local state
        setAccounts(prev => prev.map(a => a.id === accountId ? { ...a, app_id: newAppId } : a));
        console.log(`📦 Account ${accountId} → ${newAppId}`);
      }
    } catch (error) {
      console.error('❌ Erreur changement package:', error);
      alert('❌ Erreur lors du changement de package: ' + (error.response?.data?.error || error.message));
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await axios.get('/api/devices');
      if (response.data && Array.isArray(response.data.devices)) {
        setDevices(response.data.devices);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des devices:', error);
    }
  };

  const handleAddAccount = async () => {
    try {
      const payload = {
        username: newAccount.username.replace(/^@/, '').trim(),
        password: newAccount.password,
        account_type: (newAccount.account_type || 'standard').toUpperCase()
      };
      if (!payload.username || !payload.password) {
        alert('⚠️ Username et mot de passe sont requis');
        return;
      }
      const response = await axios.post('/api/accounts', payload);
      if (response.data.success) {
        setShowAddModal(false);
        setNewAccount({ username: '', password: '', account_type: 'standard' });
        fetchAccounts();
        fetchAllInstagramAccounts();
        alert('✅ Compte ajouté avec succès !');
      } else {
        alert('❌ ' + (response.data.error || 'Erreur inconnue'));
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout du compte:', error);
      alert('❌ ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAssociateAccount = async () => {
    try {
      if (!association.account_id || !association.device_id) {
        alert('⚠️ Veuillez sélectionner un compte et un device');
        return;
      }

      const response = await axios.post('/api/associate-account-device', {
        account_id: parseInt(association.account_id),
        device_id: association.device_id
      });

      if (response.data.success) {
        setShowAssociateModal(false);
        setAssociation({ account_id: '', device_id: '' });
        fetchAccounts();
        alert('✅ Compte associé au device avec succès !');
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'association:', error);
      alert('❌ Erreur lors de l\'association: ' + (error.response?.data?.error || error.message));
    }
  };

  const openSettingsModal = async (account) => {
    setSelectedAccount(account);
    // Charger les settings existants si disponibles
    try {
      const response = await axios.get(`/api/account-device-settings/${account.id}`);
      if (response.data.settings) {
        setDeviceSettings(response.data.settings);
      }
    } catch (error) {
      console.log('Aucun settings existant, utilisation des valeurs par défaut');
    }
    setShowSettingsModal(true);
  };

  const handleSaveSettings = async () => {
    try {
      const response = await axios.post(`/api/account-device-settings/${selectedAccount.id}`, deviceSettings);
      if (response.data.success) {
        setShowSettingsModal(false);
        fetchAccounts();
        alert('✅ Paramètres sauvegardés avec succès !');
      }
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde des paramètres');
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      // Définir l'ordre initial selon la colonne
      if (field === 'today_likes' || field === 'today_follows' || field === 'today_unfollows' || 
          field === 'today_story_likes' || field === 'today_story_views' || field === 'today_comments' || field === 'total_profile_views' ||
          field === 'private_profile_views' || field === 'today_unique_users' || field === 'today_scraped') {
        setSortOrder('desc'); // Pour les stats, commencer par DESC (plus grand en premier)
      } else if (field === 'device_connected') {
        setSortOrder('desc'); // Pour le statut, commencer par DESC (connectés en premier)
      } else if (field === 'is_restarter') {
        setSortOrder('desc'); // Pour restart, commencer par DESC (restarteurs en premier)
      } else {
        setSortOrder('asc'); // Pour les autres colonnes, ordre alphabétique croissant
      }
    }
  };

  const sortedAccounts = [...accounts].sort((a, b) => {
    // Tri par défaut: workstation active en premier, puis connectés, puis likes/jour (décroissant)
    if (sortBy === 'default') {
      // 1. Workstation active en premier
      if (activeWorkstation && activeWorkstation !== 'all') {
        const aIsActiveWs = activeWorkstation === 'local' 
          ? (a.workstation_id === null || a.workstation_id === undefined)
          : String(a.workstation_id) === String(activeWorkstation);
        const bIsActiveWs = activeWorkstation === 'local'
          ? (b.workstation_id === null || b.workstation_id === undefined)
          : String(b.workstation_id) === String(activeWorkstation);
        if (aIsActiveWs !== bIsActiveWs) return bIsActiveWs ? 1 : -1;
      }
      // 2. Connectés en premier
      const aConnected = a.device_connected ? 1 : 0;
      const bConnected = b.device_connected ? 1 : 0;
      if (aConnected !== bConnected) return bConnected - aConnected;
      // 3. Par likes/jour (décroissant)
      return (b.today_likes || 0) - (a.today_likes || 0);
    }
    
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    // Gestion des colonnes spéciales
    if (sortBy === 'device_connected') {
      aValue = a.device_connected ? 1 : 0;
      bValue = b.device_connected ? 1 : 0;
    }
    
    // Gestion des colonnes d'actions (stats du jour)
    if (['today_profile_views', 'today_likes', 'today_follows', 'today_unfollows', 'today_story_likes', 'today_story_views', 'today_comments', 'today_private_profile_views', 'today_unique_users', 'today_scraped'].includes(sortBy)) {
      aValue = aValue || 0;
      bValue = bValue || 0;
    }
    
    // Gestion de la colonne is_restarter
    if (sortBy === 'is_restarter') {
      aValue = a.is_restarter ? 1 : 0;
      bValue = b.is_restarter ? 1 : 0;
    }

    // Gestion des valeurs null pour les strings
    if (typeof aValue === 'string' && aValue !== null) {
      aValue = aValue.toLowerCase();
    } else if (aValue === null || aValue === undefined) {
      aValue = '';
    }
    
    if (typeof bValue === 'string' && bValue !== null) {
      bValue = bValue.toLowerCase();
    } else if (bValue === null || bValue === undefined) {
      bValue = '';
    }

    // Comparaison
    if (sortOrder === 'asc') {
      if (aValue < bValue) return -1;
      if (aValue > bValue) return 1;
      return 0;
    } else {
      if (aValue > bValue) return -1;
      if (aValue < bValue) return 1;
      return 0;
    }
  });

  // Filtrer les comptes par type de client et statut de connexion
  let filteredAccounts = selectedClientTypes.length === 0 
    ? sortedAccounts 
    : sortedAccounts.filter(account => selectedClientTypes.includes(account.account_type));
  
  // Appliquer le filtre de statut de connexion
  if (selectedConnectionStatus === 'online') {
    filteredAccounts = filteredAccounts.filter(account => account.device_connected);
  } else if (selectedConnectionStatus === 'offline') {
    filteredAccounts = filteredAccounts.filter(account => !account.device_connected);
  }

  // Appliquer le filtre par workstation
  if (selectedWorkstations.length > 0) {
    filteredAccounts = filteredAccounts.filter(account => selectedWorkstations.includes(account.workstation_name || 'Local'));
  }

  // Appliquer le filtre par statut de script
  if (selectedRunningStatus === 'running') {
    filteredAccounts = filteredAccounts.filter(account => runningScripts[account.id]);
  } else if (selectedRunningStatus === 'stopped') {
    filteredAccounts = filteredAccounts.filter(account => !runningScripts[account.id]);
  }

  // Calculer les stats sur les comptes filtrés
  const totalAccounts = filteredAccounts.length;
  const onlineAccounts = filteredAccounts.filter(a => a.device_connected).length;
  const runningCount = accounts.filter(a => runningScripts[a.id]).length;
  const stoppedCount = accounts.length - runningCount;
  
  // Compter les comptes par type dynamiquement (sur TOUS les comptes)
  const accountsByType = accounts.reduce((acc, account) => {
    const type = account.account_type || 'NON_DÉFINI';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  // Compter les comptes par workstation
  const accountsByWorkstation = accounts.reduce((acc, account) => {
    const ws = account.workstation_name || 'Local';
    acc[ws] = (acc[ws] || 0) + 1;
    return acc;
  }, {});

  // Toggle la sélection d'un type de client
  const toggleClientType = (type) => {
    setSelectedClientTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Calculer le total des actions du jour (sur les comptes filtrés)
  const todayActions = filteredAccounts.reduce((acc, account) => {
    acc.likes += account.today_likes || 0;
    acc.follows += account.today_follows || 0;
    acc.unfollows += account.today_unfollows || 0;
    acc.comments += account.today_comments || 0;
    acc.storyLikes += account.today_story_likes || 0;
    acc.storyViews += account.today_story_views || 0;
    acc.profileViews += account.today_profile_views || 0;
    acc.scraped += account.today_scraped || 0;
    return acc;
  }, { likes: 0, follows: 0, unfollows: 0, comments: 0, storyLikes: 0, storyViews: 0, profileViews: 0, scraped: 0 });

  // Navigate to automation page for account
  const navigateToAutomation = (account) => {
    if (onNavigateToAutomation) {
      onNavigateToAutomation({
        device_id: account.device_id,
        device_name: account.device_name,
        instagram_username: account.username,
        account_id: account.id
      });
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <SharedPageHeader icon={Instagram} title="Instagram Accounts" subtitle="Gestion des comptes Instagram" />
        <div style={{ textAlign: 'center', padding: '50px', color: 'var(--text-muted)' }}>
          Chargement des comptes...
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* ── Top Banner ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 24px', borderRadius: 14, marginBottom: 20, background: 'linear-gradient(135deg, rgba(165,145,239,.12), rgba(165,145,239,.04))', border: '1px solid rgba(165,145,239,.2)' }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, var(--primary), var(--secondary))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
          <Instagram size={24} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 2px' }}>Instagram Accounts</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: 'var(--text-muted)' }}>
            {totalAccounts} comptes · {onlineAccounts} en ligne
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <RefreshButton onClick={fetchAccounts} style={{ fontSize: 11, padding: '8px 14px' }}>
            <RefreshCw size={13} /> Rafraîchir
          </RefreshButton>
          <AssociateButton onClick={() => setShowAssociateModal(true)} style={{ fontSize: 11, padding: '8px 14px' }}>
            <Link size={13} /> Associer
          </AssociateButton>
          <AddButton onClick={() => setShowAddModal(true)} style={{ fontSize: 11, padding: '8px 14px' }}>
            <Plus size={13} /> Ajouter
          </AddButton>
        </div>
      </div>

      {/* ── Stats Grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 16, marginBottom: 20 }}>
        {/* Left: Filters — Types, Workstations, Connection, Running */}
        <div style={{ padding: '12px 14px', borderRadius: 12, background: 'var(--card-bg)', border: '1px solid var(--card-border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Row 1: Account types */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Shield size={12} /> Types
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {Object.entries(accountsByType).sort(([,a], [,b]) => b - a).map(([type, count]) => {
                const getTypeColor = (t) => {
                  const l = t.toLowerCase();
                  if (l === 'client') return '#a591ef';
                  if (l === 'client2') return '#e8925f';
                  if (l === 'personnel') return '#3b82f6';
                  if (l === 'impulse') return '#ef4444';
                  if (l === 'old') return '#f59e0b';
                  if (l === 'our') return '#a591ef';
                  if (l === 'test') return '#22c55e';
                  if (l === 'backup') return '#f59e0b';
                  if (l === 'standard') return '#6b7280';
                  if (l === 'timo') return '#e8925f';
                  if (l === 'qlickr') return '#22c55e';
                  return '#6b7280';
                };
                const color = getTypeColor(type);
                const isSelected = selectedClientTypes.length === 0 || selectedClientTypes.includes(type);
                return (
                  <div key={type} onClick={() => toggleClientType(type)}
                    style={{
                      padding: '4px 10px', borderRadius: 6, cursor: 'pointer', transition: 'all .15s',
                      background: isSelected ? `${color}22` : 'transparent',
                      border: `1.5px solid ${isSelected ? `${color}55` : 'var(--card-border)'}`,
                      opacity: isSelected ? 1 : 0.4, display: 'flex', alignItems: 'center', gap: 5
                    }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: isSelected ? color : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.3px' }}>
                      {type === 'NON_DÉFINI' ? 'N/A' : type}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: isSelected ? color : 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Row 2: Workstations */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Monitor size={12} /> Workstations
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {Object.entries(accountsByWorkstation).sort(([,a], [,b]) => b - a).map(([ws, count]) => {
                const wsLower = ws.toLowerCase();
                const wsColor = wsLower.includes('raspberry') || wsLower.includes('rpi') ? '#22c55e' : wsLower.includes('laptop') || wsLower.includes('portable') ? '#a78bfa' : '#3b82f6';
                const isSelected = selectedWorkstations.length === 0 || selectedWorkstations.includes(ws);
                return (
                  <div key={ws} onClick={() => setSelectedWorkstations(prev => prev.includes(ws) ? prev.filter(w => w !== ws) : [...prev, ws])}
                    style={{
                      padding: '4px 10px', borderRadius: 6, cursor: 'pointer', transition: 'all .15s',
                      background: isSelected ? `${wsColor}22` : 'transparent',
                      border: `1.5px solid ${isSelected ? `${wsColor}55` : 'var(--card-border)'}`,
                      opacity: isSelected ? 1 : 0.4, display: 'flex', alignItems: 'center', gap: 5
                    }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: isSelected ? wsColor : 'var(--text-muted)', letterSpacing: '.3px' }}>
                      {ws}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: isSelected ? wsColor : 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Row 3: Connection + Running status */}
          <div style={{ display: 'flex', gap: 12, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', gap: 5 }}>
              {[
                { key: 'all', label: 'TOUS', count: accounts.length, color: '#a591ef' },
                { key: 'online', label: 'EN LIGNE', count: accounts.filter(a => a.device_connected).length, color: '#22c55e' },
                { key: 'offline', label: 'HORS LIGNE', count: accounts.filter(a => !a.device_connected).length, color: '#ef4444' }
              ].map(({ key, label, count, color }) => {
                const active = selectedConnectionStatus === key;
                return (
                  <div key={key} onClick={() => setSelectedConnectionStatus(key)}
                    style={{
                      padding: '4px 10px', borderRadius: 6, cursor: 'pointer', transition: 'all .15s',
                      background: active ? `${color}22` : 'transparent',
                      border: `1.5px solid ${active ? `${color}55` : 'var(--card-border)'}`,
                      opacity: active ? 1 : 0.4, display: 'flex', alignItems: 'center', gap: 5
                    }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: active ? color : 'var(--text-muted)', letterSpacing: '.3px' }}>{label}</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: active ? color : 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>{count}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ width: 1, background: 'var(--border)' }} />
            <div style={{ display: 'flex', gap: 5 }}>
              {[
                { key: 'all', label: 'TOUS', count: accounts.length, color: '#a591ef', icon: null },
                { key: 'running', label: 'EN COURS', count: runningCount, color: '#22c55e', icon: <Play size={10} /> },
                { key: 'stopped', label: 'ARRÊTÉS', count: stoppedCount, color: '#ef4444', icon: <X size={10} /> }
              ].map(({ key, label, count, color, icon }) => {
                const active = selectedRunningStatus === key;
                return (
                  <div key={`run-${key}`} onClick={() => setSelectedRunningStatus(key)}
                    style={{
                      padding: '4px 10px', borderRadius: 6, cursor: 'pointer', transition: 'all .15s',
                      background: active ? `${color}22` : 'transparent',
                      border: `1.5px solid ${active ? `${color}55` : 'var(--card-border)'}`,
                      opacity: active ? 1 : 0.4, display: 'flex', alignItems: 'center', gap: 5
                    }}>
                    {icon && <span style={{ color: active ? color : 'var(--text-muted)', display: 'flex' }}>{icon}</span>}
                    <span style={{ fontSize: 10, fontWeight: 700, color: active ? color : 'var(--text-muted)', letterSpacing: '.3px' }}>{label}</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: active ? color : 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right: Daily actions — compact grid */}
        <div style={{ padding: '12px 14px', borderRadius: 12, background: 'var(--card-bg)', border: '1px solid var(--card-border)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Heart size={11} /> Actions du jour
            <span style={{ marginLeft: 'auto', fontSize: 18, fontWeight: 800, color: 'var(--primary)', fontFamily: "'JetBrains Mono', monospace" }}>
              {todayActions.profileViews + todayActions.likes + todayActions.follows + todayActions.unfollows + todayActions.storyLikes + todayActions.storyViews + todayActions.comments}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 5 }}>
            {[
              { icon: <Eye size={11} />, val: todayActions.profileViews, label: 'Profils', color: '#3b82f6' },
              { icon: <Heart size={11} />, val: todayActions.likes, label: 'Likes', color: '#ef4444' },
              { icon: <UserPlus size={11} />, val: todayActions.follows, label: 'Follows', color: '#22c55e' },
              { icon: <UserMinus size={11} />, val: todayActions.unfollows, label: 'Unfollows', color: '#f59e0b' },
              { icon: <Heart size={11} />, val: todayActions.storyLikes, label: 'Story Likes', color: '#8b5cf6' },
              { icon: <Film size={11} />, val: todayActions.storyViews, label: 'Stories vues', color: '#ec4899' },
              { icon: <MessageCircle size={11} />, val: todayActions.comments, label: 'Comments', color: '#06b6d4' },
              { icon: <Users size={11} />, val: filteredAccounts.reduce((s, a) => s + (a.today_unique_users || 0), 0), label: 'Uniques', color: '#a591ef' },
              { icon: <Lock size={11} />, val: filteredAccounts.reduce((s, a) => s + (a.today_private_profile_views || 0), 0), label: 'Privés', color: '#ef4444' },
              { icon: <Target size={11} />, val: todayActions.scraped, label: 'Scrappés', color: '#a855f7' }
            ].map(({ icon, val, label, color }) => (
              <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '6px 4px', borderRadius: 8, background: `${color}0d`, gap: 2 }}>
                <span style={{ color, display: 'flex', opacity: 0.8 }}>{icon}</span>
                <span style={{ fontSize: 15, fontWeight: 800, color, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>{val}</span>
                <span style={{ fontSize: 7, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.3px', lineHeight: 1 }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <AccountsTable>
        {(() => {
          const gridCols = `26px 34px 24px 60px 34px 30px 26px 58px 34px minmax(80px,1fr) 38px ${showLikeBar ? '54px' : '18px'} 38px ${showFollowBar ? '54px' : '18px'} 38px 34px 38px 38px 38px 38px 38px 40px`;
          return (<>
        <TableHeader style={{ gridTemplateColumns: gridCols }}>
          {/* 1. Workstation */}
          <TableHeaderCell onClick={() => handleSort('workstation_name')} style={{ justifyContent: 'center' }} title="Workstation">
            <Monitor size={10} />
          </TableHeaderCell>
          {/* 2. ID */}
          <TableHeaderCell onClick={() => handleSort('id')} title="ID" style={{ justifyContent: 'center' }}>
            <Hash size={11} />
          </TableHeaderCell>
          {/* 3. Status */}
          <TableHeaderCell onClick={() => handleSort('device_connected')} style={{ justifyContent: 'center' }} title="Statut">
            <CheckCircle size={11} />
          </TableHeaderCell>
          {/* 4. Device badge */}
          <TableHeaderCell onClick={() => handleSort('device_name')} title="Device" style={{ justifyContent: 'center' }}>
            <Cast size={11} />
          </TableHeaderCell>
          {/* 4b. APK Package */}
          <TableHeaderCell onClick={() => handleSort('app_id')} title="APK" style={{ justifyContent: 'center' }}>
            <span style={{ fontSize: 9, fontWeight: 800 }}>APK</span>
          </TableHeaderCell>
          {/* Settings */}
          <TableHeaderCell style={{ justifyContent: 'center' }} title="Settings">
            <Settings size={11} />
          </TableHeaderCell>
          {/* Screen mirror */}
          <TableHeaderCell style={{ justifyContent: 'center' }} title="Mirror">
            <Eye size={10} />
          </TableHeaderCell>
          {/* Type */}
          <TableHeaderCell onClick={() => handleSort('account_type')} title="Type" style={{ justifyContent: 'center' }}>
            <Shield size={11} />
          </TableHeaderCell>
          {/* Photo */}
          <TableHeaderCell title="Photo" />
          {/* 7. Username */}
          <TableHeaderCell onClick={() => handleSort('username')} title="Username">
            <Instagram size={11} style={{ color: '#E1306C' }} />
            {sortBy === 'username' && (sortOrder === 'asc' ? <ArrowUp size={9} /> : <ArrowDown size={9} />)}
          </TableHeaderCell>
          {/* 8. Likes */}
          <TableHeaderCell onClick={() => handleSort('today_likes')} style={{ justifyContent: 'center' }} title="Likes">
            <Heart size={12} style={{ color: 'var(--accent)' }} />
          </TableHeaderCell>
          {/* 9. Like completion bar toggle */}
          <TableHeaderCell
            style={{ justifyContent: 'center', cursor: 'pointer', padding: 0, minWidth: 0, overflow: 'hidden' }}
            onClick={() => setShowLikeBar(!showLikeBar)}
            title={showLikeBar ? "Masquer barre likes" : "Afficher barre likes"}
          >
            <input type="checkbox" checked={showLikeBar} readOnly
              style={{ width: 10, height: 10, cursor: 'pointer', accentColor: 'var(--accent)', margin: 0 }} />
          </TableHeaderCell>
          {/* 10. Follows */}
          <TableHeaderCell onClick={() => handleSort('today_follows')} style={{ justifyContent: 'center' }} title="Follows">
            <UserPlus size={12} style={{ color: 'var(--primary)' }} />
          </TableHeaderCell>
          {/* 11. Follow completion bar toggle */}
          <TableHeaderCell
            style={{ justifyContent: 'center', cursor: 'pointer', padding: 0, minWidth: 0, overflow: 'hidden' }}
            onClick={() => setShowFollowBar(!showFollowBar)}
            title={showFollowBar ? "Masquer barre follows" : "Afficher barre follows"}
          >
            <input type="checkbox" checked={showFollowBar} readOnly
              style={{ width: 10, height: 10, cursor: 'pointer', accentColor: 'var(--primary)', margin: 0 }} />
          </TableHeaderCell>
          {/* 12. Profile views */}
          <TableHeaderCell onClick={() => handleSort('today_profile_views')} style={{ justifyContent: 'center' }} title="Profils vus">
            <Eye size={12} style={{ color: 'var(--info)' }} />
          </TableHeaderCell>
          <TableHeaderCell onClick={() => handleSort('today_private_profile_views')} style={{ justifyContent: 'center' }} title="Profils privés">
            <Lock size={12} style={{ color: 'var(--error)' }} />
          </TableHeaderCell>
          <TableHeaderCell onClick={() => handleSort('today_unique_users')} style={{ justifyContent: 'center' }} title="Users uniques">
            <Users size={12} style={{ color: 'var(--primary)' }} />
          </TableHeaderCell>
          <TableHeaderCell onClick={() => handleSort('today_story_likes')} style={{ justifyContent: 'center' }} title="Story Likes">
            <Heart size={12} style={{ color: '#8b5cf6' }} />
          </TableHeaderCell>
          <TableHeaderCell onClick={() => handleSort('today_story_views')} style={{ justifyContent: 'center' }} title="Stories vues">
            <Film size={12} style={{ color: '#ec4899' }} />
          </TableHeaderCell>
          <TableHeaderCell onClick={() => handleSort('today_unfollows')} style={{ justifyContent: 'center' }} title="Unfollows">
            <UserMinus size={12} style={{ color: 'var(--accent)' }} />
          </TableHeaderCell>
          <TableHeaderCell onClick={() => handleSort('today_scraped')} style={{ justifyContent: 'center' }} title="Scrappés">
            <Target size={12} style={{ color: '#a855f7' }} />
          </TableHeaderCell>
          {/* Workflow */}
          <TableHeaderCell onClick={() => handleSort('workflow_character')} style={{ justifyContent: 'center' }} title="Workflow">
            <RotateCw size={11} />
          </TableHeaderCell>
        </TableHeader>

        {filteredAccounts.map(account => {
          const isRunning = runningScripts[account.id];
          const inWorkflow = account.workflow_character && account.workflow_character !== '-';
          const wsName = (account.workstation_name || '').toLowerCase();
          const isRpi = wsName.includes('raspberry') || wsName.includes('rpi');
          const isLaptop = wsName.includes('laptop') || wsName.includes('portable');
          const WsIcon = isRpi ? Server : isLaptop ? Laptop : Monitor;
          const wsColor = isRpi ? '#22c55e' : isLaptop ? '#a78bfa' : 'var(--info)';
          const likeTarget = account.like_target_for_the_day || 0;
          const likeDone = account.today_likes || 0;
          const likePct = likeTarget > 0 ? Math.min(100, Math.round((likeDone / likeTarget) * 100)) : 0;
          const followTarget = account.follow_target_for_the_day || 0;
          const followDone = account.today_follows || 0;
          const followPct = followTarget > 0 ? Math.min(100, Math.round((followDone / followTarget) * 100)) : 0;
          
          return (
          <React.Fragment key={account.id}>
          <TableRow 
            $active={account.device_connected}
            $isRunning={isRunning}
            $inWorkflow={inWorkflow && !isRunning}
            $accountType={account.account_type}
            onClick={() => navigateToAutomation(account)}
            style={{ cursor: 'pointer', gridTemplateColumns: gridCols }}
          >
            {/* 1. Workstation icon */}
            <Cell style={{ justifyContent: 'center' }} title={account.workstation_name || 'Local'}>
              <WsIcon size={13} style={{ color: wsColor, opacity: account.workstation_name ? 1 : 0.3 }} />
            </Cell>
            {/* 2. ID */}
            <Cell style={{ justifyContent: 'center', fontSize: 11, color: 'var(--text-muted)' }}>{account.id}</Cell>
            {/* 3. Status */}
            <Cell style={{ justifyContent: 'center' }}>
              <StatusIndicator $active={account.device_connected} />
            </Cell>
            {/* 4. Device badge */}
            <Cell style={{ justifyContent: 'center' }}>
              {account.device_name ? (
                <DeviceBadge style={{ fontSize: 9, padding: '3px 6px' }}>{account.device_name}</DeviceBadge>
              ) : <span style={{ color: 'var(--text-muted)', fontSize: 9 }}>—</span>}
            </Cell>
            {/* 4b. APK Package badge */}
            <Cell style={{ justifyContent: 'center' }}>
              {(() => {
                const pkg = instagramPackages.find(p => p.package_name === account.app_id);
                const color = pkg ? pkg.color : '#6366f1';
                const isDefault = !account.app_id || account.app_id === 'com.instagram.android';
                return (
                  <select
                    value={account.app_id || 'com.instagram.android'}
                    onChange={(e) => { e.stopPropagation(); handleChangeAppId(account.id, e.target.value); }}
                    onClick={(e) => e.stopPropagation()}
                    title={account.app_id || 'com.instagram.android'}
                    style={{
                      width: 34, height: 20, fontSize: 9, fontWeight: 800,
                      background: isDefault ? 'var(--surface)' : `${color}22`,
                      color: isDefault ? 'var(--text-muted)' : color,
                      border: `1.5px solid ${isDefault ? 'var(--border)' : color}`,
                      borderRadius: 4, cursor: 'pointer', textAlign: 'center',
                      padding: 0, appearance: 'none', WebkitAppearance: 'none',
                      outline: 'none'
                    }}
                  >
                    {instagramPackages.map(p => (
                      <option key={p.package_name} value={p.package_name}>
                        {p.suffix_letter === '-' ? 'IG' : p.suffix_letter}
                      </option>
                    ))}
                    {instagramPackages.length === 0 && (
                      <option value="com.instagram.android">IG</option>
                    )}
                  </select>
                );
              })()}
            </Cell>
            {/* Settings */}
            <Cell style={{ justifyContent: 'center' }}>
              <SettingsButton onClick={(e) => { e.stopPropagation(); openSettingsModal(account); }} style={{ width: 28, height: 28, borderRadius: 6 }}>
                <Settings size={13} />
              </SettingsButton>
            </Cell>
            {/* Screen mirror button */}
            <Cell style={{ justifyContent: 'center' }}>
              <CastButton 
                disabled={!account.device_connected || !account.device_id}
                onClick={(e) => {
                  e.stopPropagation();
                  if (account.device_connected && account.device_id && onSelectDevice && setMirrorOpen) {
                    // If mirror is already open for THIS device, toggle it off
                    const currentDevice = window.__mirrorDeviceId;
                    if (currentDevice === account.device_id) {
                      setMirrorOpen(false);
                      window.__mirrorDeviceId = null;
                    } else {
                      // Open mirror for this device (or switch to it)
                      onSelectDevice({ device_id: account.device_id, device_name: account.device_name, instagram_username: account.username, account_id: account.id, isConnected: true });
                      setMirrorOpen(true);
                      window.__mirrorDeviceId = account.device_id;
                    }
                  }
                }}
                title={account.device_connected ? 'Screen Mirror' : 'Hors ligne'}
                style={{ width: 24, height: 24, borderRadius: 5 }}
              >
                <Cast size={12} />
              </CastButton>
            </Cell>
            {/* Type */}
            <Cell style={{ justifyContent: 'center' }}>
              <AccountTypeCell type={account.account_type?.toLowerCase()} style={{ fontSize: 9, padding: '4px 8px', letterSpacing: 0.5 }}>
                {account.account_type || '—'}
              </AccountTypeCell>
            </Cell>
            {/* Photo */}
            <Cell>
              {account.username ? (
                <ProfileImage 
                  src={getLocalProfilePicUrl(account.username)}
                  alt={account.username}
                  style={{ width: 30, height: 30 }}
                  onClick={(e) => { e.stopPropagation(); onNavigateToStats && onNavigateToStats(account.id); }}
                  onError={(e) => {
                    const step = parseInt(e.target.dataset.fallback || '0');
                    if (step === 0) { e.target.dataset.fallback = '1'; e.target.src = getProfilePicUrl(account.username); }
                    else if (step === 1) { e.target.dataset.fallback = '2'; e.target.src = `https://unavatar.io/instagram/${account.username}`; }
                    else { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }
                  }}
                />
              ) : null}
              <ProfilePlaceholder style={{ display: 'none', width: 30, height: 30, fontSize: 10 }}>{account.username ? account.username.charAt(0).toUpperCase() : '?'}</ProfilePlaceholder>
            </Cell>
            {/* 7. Username */}
            <UsernameCell>
              <Instagram size={13} style={{ color: '#E1306C', flexShrink: 0 }} />
              <span>@{account.username}</span>
            </UsernameCell>
            {/* 8. Likes */}
            <Cell style={{ justifyContent: 'center', color: 'var(--accent)', fontWeight: 800, fontSize: 13 }}>{likeDone}</Cell>
            {/* 9. Like completion bar */}
            <Cell style={{ justifyContent: 'center', overflow: 'hidden', padding: showLikeBar ? '0 3px' : 0, minWidth: 0 }}>
              {showLikeBar && (
                <div style={{ width: '100%', textAlign: 'center' }} title={`${likeDone}/${likeTarget} likes`}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: likePct >= 100 ? '#22c55e' : 'var(--accent)', lineHeight: 1, marginBottom: 2 }}>{likePct}%</div>
                  <div style={{ width: '100%', height: 3, borderRadius: 2, background: 'rgba(255,255,255,.08)' }}>
                    <div style={{ width: `${likePct}%`, height: '100%', borderRadius: 2, background: likePct >= 100 ? '#22c55e' : 'var(--accent)', transition: 'width .3s' }} />
                  </div>
                </div>
              )}
            </Cell>
            {/* 10. Follows */}
            <Cell style={{ justifyContent: 'center', color: 'var(--primary)', fontWeight: 800, fontSize: 13 }}>{followDone}</Cell>
            {/* 11. Follow completion bar */}
            <Cell style={{ justifyContent: 'center', overflow: 'hidden', padding: showFollowBar ? '0 3px' : 0, minWidth: 0 }}>
              {showFollowBar && (
                <div style={{ width: '100%', textAlign: 'center' }} title={`${followDone}/${followTarget} follows`}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: followPct >= 100 ? '#22c55e' : 'var(--primary)', lineHeight: 1, marginBottom: 2 }}>{followPct}%</div>
                  <div style={{ width: '100%', height: 3, borderRadius: 2, background: 'rgba(255,255,255,.08)' }}>
                    <div style={{ width: `${followPct}%`, height: '100%', borderRadius: 2, background: followPct >= 100 ? '#22c55e' : 'var(--primary)', transition: 'width .3s' }} />
                  </div>
                </div>
              )}
            </Cell>
            {/* 12-19. Other stats */}
            <Cell style={{ justifyContent: 'center', color: 'var(--info)', fontWeight: 800, fontSize: 13 }}>{account.today_profile_views || 0}</Cell>
            <Cell style={{ justifyContent: 'center', color: 'var(--error)', fontWeight: 800, fontSize: 13 }}>{account.today_private_profile_views || 0}</Cell>
            <Cell style={{ justifyContent: 'center', color: 'var(--primary)', fontWeight: 800, fontSize: 13 }}>{account.today_unique_users || 0}</Cell>
            <Cell style={{ justifyContent: 'center', color: '#8b5cf6', fontWeight: 800, fontSize: 13 }}>{account.today_story_likes || 0}</Cell>
            <Cell style={{ justifyContent: 'center', color: '#ec4899', fontWeight: 800, fontSize: 13 }}>{account.today_story_views || 0}</Cell>
            <Cell style={{ justifyContent: 'center', color: 'var(--accent)', fontWeight: 800, fontSize: 13 }}>{account.today_unfollows || 0}</Cell>
            <Cell style={{ justifyContent: 'center', color: '#a855f7', fontWeight: 800, fontSize: 13 }}>{account.today_scraped || 0}</Cell>
            {/* Workflow */}
            <Cell style={{ justifyContent: 'center' }}>
              <WorkflowBadge $workflow={account.workflow_character} style={{ fontSize: 11, padding: '4px 10px' }}>
                {account.workflow_character || '-'}
              </WorkflowBadge>
            </Cell>
          </TableRow>
          </React.Fragment>
          );
        })}
        </>);
        })()}
      </AccountsTable>

      {showAddModal && (
        <ModalOverlay onClick={() => setShowAddModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <Instagram size={28} />
                Ajouter un compte Instagram
              </ModalTitle>
              <CloseButton onClick={() => setShowAddModal(false)}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <FormGroup>
                <Label>Username Instagram</Label>
                <Input
                  type="text"
                  placeholder="@username"
                  value={newAccount.username}
                  onChange={(e) => setNewAccount({ ...newAccount, username: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>Mot de passe</Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={newAccount.password}
                  onChange={(e) => setNewAccount({ ...newAccount, password: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>Type de compte</Label>
                <Select
                  value={newAccount.account_type}
                  onChange={(e) => setNewAccount({ ...newAccount, account_type: e.target.value })}
                >
                  <option value="client">Client</option>
                  <option value="client2">Client2</option>
                  <option value="personnel">Personnel</option>
                  <option value="impulse">Impulse</option>
                  <option value="old">Old</option>
                  <option value="our">Our</option>
                  <option value="test">Test</option>
                  <option value="backup">Backup</option>
                  <option value="standard">Standard</option>
                  <option value="timo">Timo</option>
                </Select>
              </FormGroup>

            </ModalBody>

            <ModalFooter>
              <CancelButton onClick={() => setShowAddModal(false)}>
                Annuler
              </CancelButton>
              <SubmitButton onClick={handleAddAccount}>
                <Plus size={18} />
                Ajouter le compte
              </SubmitButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {showAssociateModal && (
        <ModalOverlay onClick={() => setShowAssociateModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <Link size={28} />
                Associer un compte à un device
              </ModalTitle>
              <CloseButton onClick={() => setShowAssociateModal(false)}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <FormGroup>
                <Label>Compte Instagram</Label>
                <div style={{ position: 'relative' }}>
                  <div
                    onClick={() => setAssociateDropdownOpen(!associateDropdownOpen)}
                    style={{
                      width: '100%', padding: '10px 16px', background: 'var(--bg)',
                      border: '1px solid var(--card-border)', borderRadius: '8px',
                      color: 'var(--text-primary)', fontSize: '14px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '10px',
                      transition: 'border-color .2s'
                    }}
                  >
                    {association.account_id ? (() => {
                      const sel = allInstagramAccounts.find(a => String(a.id) === String(association.account_id));
                      return sel ? (
                        <>
                          <img src={getLocalProfilePicUrl(sel.username)} alt=""
                            onError={e => {
                              const step = parseInt(e.target.dataset.fb || '0');
                              if (step === 0) { e.target.dataset.fb = '1'; e.target.src = getProfilePicUrl(sel.username); }
                              else if (step === 1) { e.target.dataset.fb = '2'; e.target.src = `https://unavatar.io/instagram/${sel.username}`; }
                              else e.target.style.display = 'none';
                            }}
                            style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--primary)' }} />
                          <span style={{ fontWeight: 700, color: '#fff' }}>@{sel.username}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>ID: {sel.id}</span>
                        </>
                      ) : 'Compte inconnu';
                    })() : <span style={{ color: 'var(--text-muted)' }}>Sélectionner un compte...</span>}
                    <ChevronDown size={16} style={{ marginLeft: 'auto', color: 'var(--text-muted)' }} />
                  </div>
                  {associateDropdownOpen && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                      background: 'var(--card-bg)', border: '1px solid var(--primary)',
                      borderRadius: '8px', marginTop: '4px', maxHeight: '280px', overflowY: 'auto',
                      boxShadow: '0 8px 32px rgba(0,0,0,.4)'
                    }}>
                      {allInstagramAccounts.map(account => (
                        <div key={account.id}
                          onClick={() => { setAssociation({ ...association, account_id: String(account.id) }); setAssociateDropdownOpen(false); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '10px 14px', cursor: 'pointer',
                            borderBottom: '1px solid var(--card-border)',
                            transition: 'background .15s',
                            background: String(association.account_id) === String(account.id) ? 'rgba(165,145,239,.15)' : 'transparent'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(165,145,239,.1)'}
                          onMouseLeave={e => e.currentTarget.style.background = String(association.account_id) === String(account.id) ? 'rgba(165,145,239,.15)' : 'transparent'}
                        >
                          <img src={getLocalProfilePicUrl(account.username)} alt=""
                            onError={e => {
                              const step = parseInt(e.target.dataset.fb || '0');
                              if (step === 0) { e.target.dataset.fb = '1'; e.target.src = getProfilePicUrl(account.username); }
                              else if (step === 1) { e.target.dataset.fb = '2'; e.target.src = `https://unavatar.io/instagram/${account.username}`; }
                              else { e.target.style.display='none'; if(e.target.nextSibling) e.target.nextSibling.style.display='flex'; }
                            }}
                            style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--primary)', flexShrink: 0 }} />
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--primary)', display: 'none', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                            {(account.username || '?').charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>@{account.username || '???'}</div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>ID: {account.id}</div>
                          </div>
                        </div>
                      ))}
                      {allInstagramAccounts.length === 0 && (
                        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                          Aucun compte disponible
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Device</Label>
                <Select
                  value={association.device_id}
                  onChange={(e) => setAssociation({ ...association, device_id: e.target.value })}
                >
                  <option value="">Sélectionner un device</option>
                  {devices.map(device => (
                    <option key={device.device_id} value={device.device_id}>
                      {device.device_name ? device.device_name : device.device_id}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <div style={{ 
                background: 'var(--surface)', 
                border: '1px solid var(--border)', 
                borderRadius: '8px', 
                padding: '15px', 
                marginTop: '20px' 
              }}>
                <div style={{ color: 'var(--primary)', fontWeight: '600', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Instagram size={18} />
                  Paramètres par défaut qui seront créés
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: '1.8', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>• Status: <strong>inactive</strong></div>
                  <div>• Workflow: <strong>FULL</strong></div>
                  <div>• Interactions/source: <strong>150</strong></div>
                  <div>• Likes: <strong>150</strong></div>
                  <div>• Follow %: <strong>25%</strong></div>
                  <div>• Stories: <strong>500</strong></div>
                  <div>• Story limit: <strong>500</strong></div>
                  <div>• Total likes: <strong>150</strong></div>
                  <div>• Story like %: <strong>20%</strong></div>
                  <div>• Unfollow delay: <strong>7 jours</strong></div>
                </div>
                <div style={{ marginTop: '10px', padding: '10px', background: 'rgba(72, 219, 251, 0.1)', borderRadius: '6px', fontSize: '11px', color: 'var(--info)' }}>
                  ℹ️ Vous pourrez modifier tous ces paramètres après la création via le bouton Settings
                </div>
              </div>
            </ModalBody>

            <ModalFooter>
              <CancelButton onClick={() => setShowAssociateModal(false)}>
                Annuler
              </CancelButton>
              <SubmitButton onClick={handleAssociateAccount}>
                <Link size={18} />
                Associer
              </SubmitButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {showSettingsModal && selectedAccount && (
        <ModalOverlay onClick={() => setShowSettingsModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1600px' }}>
            <ModalHeader>
              <ModalTitle>
                <Settings size={28} />
                Paramètres - @{selectedAccount.username}
              </ModalTitle>
              <CloseButton onClick={() => setShowSettingsModal(false)}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              {/* Section Générale */}
              <GeneralSection>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
                  <FormGroup>
                    <Label>Device associé</Label>
                    <Select
                      value={deviceSettings.device_id}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, device_id: e.target.value })}
                    >
                      <option value="">Aucun device</option>
                      {devices.map(device => (
                        <option key={device.id} value={device.device_id}>
                          {device.device_name || device.device_id} {device.connected ? '🟢' : '🔴'}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Statut</Label>
                    <Select
                      value={deviceSettings.status}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, status: e.target.value })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Workflow</Label>
                    <Select
                      value={deviceSettings.workflow}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, workflow: e.target.value })}
                    >
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Order Position</Label>
                    <Input
                      type="number"
                      value={deviceSettings.order_position}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, order_position: parseInt(e.target.value) })}
                    />
                  </FormGroup>
                </div>
              </GeneralSection>

              {/* 3 Sections Verticales */}
              <SettingsSections>
                {/* Section Interaction */}
                <SettingsSection>
                  <SectionHeader $color="var(--primary)">
                    <SectionIcon $color="var(--primary)">
                      <Heart size={20} />
                    </SectionIcon>
                    <SectionTitle>Interaction</SectionTitle>
                  </SectionHeader>

                  <FormGroup>
                    <Label>Interact Targets</Label>
                    <Input
                      type="text"
                      placeholder="[null]"
                      value={deviceSettings.interact_targets}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, interact_targets: e.target.value })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Interactions Limit Per Source</Label>
                    <Input
                      type="text"
                      placeholder="2-3"
                      value={deviceSettings.successful_interactions_limit_per_source}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, successful_interactions_limit_per_source: e.target.value })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Likes Count</Label>
                    <Input
                      type="text"
                      placeholder="10-12"
                      value={deviceSettings.likes_count}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, likes_count: e.target.value })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Total Likes Limit</Label>
                    <Input
                      type="number"
                      value={deviceSettings.total_likes_limit || ''}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, total_likes_limit: parseInt(e.target.value) || 0 })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Like Target for the Day</Label>
                    <Input
                      type="number"
                      value={deviceSettings.like_target_for_the_day}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, like_target_for_the_day: parseInt(e.target.value) })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Follow Percentage (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={deviceSettings.follow_percentage || ''}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, follow_percentage: parseInt(e.target.value) || 0 })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Follow Target for the Day</Label>
                    <Input
                      type="number"
                      value={deviceSettings.follow_target_for_the_day}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, follow_target_for_the_day: parseInt(e.target.value) })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Comment Percentage (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={deviceSettings.comment_percentage}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, comment_percentage: parseInt(e.target.value) })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Stories Count</Label>
                    <Input
                      type="text"
                      placeholder="10-12"
                      value={deviceSettings.stories_count}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, stories_count: e.target.value })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Total Story Limit</Label>
                    <Input
                      type="number"
                      value={deviceSettings.total_story_limit || ''}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, total_story_limit: parseInt(e.target.value) || 0 })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Story Like Percentage (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={deviceSettings.story_like_percentage}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, story_like_percentage: parseInt(e.target.value) })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Session Length Limit (mins) — range min-max</Label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Input
                        type="number"
                        min="1" max="480"
                        style={{ flex: 1 }}
                        value={String(deviceSettings.session_length_in_mins_limit || '').split('-')[0] || ''}
                        onChange={(e) => {
                          const lo = e.target.value;
                          const parts = String(deviceSettings.session_length_in_mins_limit || '').split('-');
                          const hi = parts[1] || parts[0] || lo;
                          setDeviceSettings({ ...deviceSettings, session_length_in_mins_limit: `${lo}-${hi}` });
                        }}
                        placeholder="Min"
                      />
                      <span style={{ color: 'var(--text-muted)', fontWeight: 700 }}>–</span>
                      <Input
                        type="number"
                        min="1" max="480"
                        style={{ flex: 1 }}
                        value={String(deviceSettings.session_length_in_mins_limit || '').split('-')[1] || String(deviceSettings.session_length_in_mins_limit || '')}
                        onChange={(e) => {
                          const hi = e.target.value;
                          const parts = String(deviceSettings.session_length_in_mins_limit || '').split('-');
                          const lo = parts[0] || hi;
                          setDeviceSettings({ ...deviceSettings, session_length_in_mins_limit: `${lo}-${hi}` });
                        }}
                        placeholder="Max"
                      />
                    </div>
                  </FormGroup>
                </SettingsSection>

                {/* Section Unfollow */}
                <SettingsSection>
                  <SectionHeader $color="var(--error)">
                    <SectionIcon $color="var(--error)">
                      <UserMinus size={20} />
                    </SectionIcon>
                    <SectionTitle>Unfollow</SectionTitle>
                  </SectionHeader>

                  <FormGroup>
                    <Label>Type de Script Unfollow ⭐</Label>
                    <Select
                      value={deviceSettings.unfollow_type_script || 'database-global-search'}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, unfollow_type_script: e.target.value })}
                    >
                      <option value="database-global-search">⭐⭐⭐ Database Global Search (Recommandé)</option>
                      <option value="database">⭐⭐ Database (via profil)</option>
                      <option value="postgres">⭐⭐ PostgreSQL List</option>
                      <option value="simple">⭐ Simple List</option>
                      <option value="direct">⭐ Direct List</option>
                      <option value="profile">⭐ Profile (par défaut)</option>
                    </Select>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '5px' }}>
                      {deviceSettings.unfollow_type_script === 'database-global-search' && '🚀 Plus rapide - Recherche via global search Instagram + filtre par date'}
                      {deviceSettings.unfollow_type_script === 'database' && '📊 Recherche via profil + filtre par date (plus lent)'}
                      {deviceSettings.unfollow_type_script === 'postgres' && '📝 Unfollow depuis fichier unfollow_postgres.txt'}
                      {deviceSettings.unfollow_type_script === 'simple' && '📝 Unfollow depuis fichier unfollow_simple.txt'}
                      {deviceSettings.unfollow_type_script === 'direct' && '📝 Unfollow depuis fichier unfollow_direct.txt'}
                      {deviceSettings.unfollow_type_script === 'profile' && '👤 Unfollow depuis la liste de followings du profil'}
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label>Unfollow Count</Label>
                    <Input
                      type="text"
                      placeholder="50-100"
                      value={deviceSettings.unfollow_count}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, unfollow_count: e.target.value })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Unfollow Older Than (days)</Label>
                    <Input
                      type="number"
                      value={deviceSettings.unfollow_older_than_days}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, unfollow_older_than_days: parseInt(e.target.value) })}
                    />
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '5px' }}>
                      Utilisé pour database-global-search et database
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label>Unfollow Limit</Label>
                    <Input
                      type="number"
                      value={deviceSettings.unfollow_limit}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, unfollow_limit: parseInt(e.target.value) })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Min Following Hours</Label>
                    <Input
                      type="number"
                      value={deviceSettings.min_following_hours}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, min_following_hours: parseInt(e.target.value) })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Unfollow Delay</Label>
                    <Input
                      type="text"
                      placeholder="10-16"
                      value={deviceSettings.unfollow_delay}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, unfollow_delay: e.target.value })}
                    />
                  </FormGroup>
                </SettingsSection>

                {/* Section SCRAPPING */}
                <SettingsSection>
                  <SectionHeader $color="var(--warning)">
                    <SectionIcon $color="var(--warning)">
                      <Users size={20} />
                    </SectionIcon>
                    <SectionTitle>SCRAPPING</SectionTitle>
                  </SectionHeader>

                  <FormGroup>
                    <Label>Limite profils total (range)</Label>
                    <Input
                      type="text"
                      placeholder="200-300"
                      value={deviceSettings.total_scrape_limit || ''}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, total_scrape_limit: e.target.value })}
                    />
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '5px' }}>
                      Format: min-max (ex: 100-200)
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label>Limite par source (range)</Label>
                    <Input
                      type="text"
                      placeholder="200-300"
                      value={deviceSettings.scrape_limit_per_source || ''}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, scrape_limit_per_source: e.target.value })}
                    />
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '5px' }}>
                      Format: min-max (ex: 50-100)
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={deviceSettings.gemini_analysis !== false}
                        onChange={(e) => setDeviceSettings({ ...deviceSettings, gemini_analysis: e.target.checked })}
                        style={{ width: '18px', height: '18px' }}
                      />
                      Analyse Gemini
                    </Label>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '5px' }}>
                      Enrichit les profils scrapés via Gemini AI
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={deviceSettings.deep_analysis || false}
                        onChange={(e) => setDeviceSettings({ ...deviceSettings, deep_analysis: e.target.checked })}
                        style={{ width: '18px', height: '18px' }}
                      />
                      Analyse approfondie (followings)
                    </Label>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '5px' }}>
                      Analyse aussi les followings des profils scrapés
                    </div>
                  </FormGroup>
                </SettingsSection>

                {/* Section DM */}
                <SettingsSection>
                  <SectionHeader $color="var(--info)">
                    <SectionIcon $color="var(--info)">
                      <MessageCircle size={20} />
                    </SectionIcon>
                    <SectionTitle>Direct Messages</SectionTitle>
                  </SectionHeader>

                  <FormGroup>
                    <Label>DM List</Label>
                    <Input
                      type="text"
                      placeholder="[null]"
                      value={deviceSettings.dm_list}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, dm_list: e.target.value })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>DM New Followers</Label>
                    <Select
                      value={deviceSettings.dm_new_followers}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, dm_new_followers: e.target.value })}
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>DM to Bot Followed</Label>
                    <Select
                      value={deviceSettings.dm_to_bot_followed}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, dm_to_bot_followed: e.target.value })}
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>DM Daily Limit</Label>
                    <Input
                      type="number"
                      value={deviceSettings.dm_daily_limit}
                      onChange={(e) => setDeviceSettings({ ...deviceSettings, dm_daily_limit: parseInt(e.target.value) })}
                    />
                  </FormGroup>
                </SettingsSection>
              </SettingsSections>
            </ModalBody>

            <ModalFooter>
              <CancelButton onClick={() => setShowSettingsModal(false)}>
                Annuler
              </CancelButton>
              <SubmitButton onClick={handleSaveSettings}>
                <CheckCircle size={18} />
                Sauvegarder
              </SubmitButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {showListsModal && (
        <ModalOverlay onClick={() => setShowListsModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <ModalHeader>
              <ModalTitle>
                <List size={28} />
                Listes - @{selectedListAccount?.username}
              </ModalTitle>
              <CloseButton onClick={() => setShowListsModal(false)}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              {/* Sélection du type de liste */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                  <button
                    onClick={() => setSelectedListType('whitelist')}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: selectedListType === 'whitelist' ? '2px solid var(--success)' : '1px solid var(--card-border)',
                      background: selectedListType === 'whitelist' ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg)',
                      color: selectedListType === 'whitelist' ? 'var(--success)' : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontWeight: '600',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <Shield size={18} />
                    Whitelist
                  </button>
                  <button
                    onClick={() => setSelectedListType('blacklist')}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: selectedListType === 'blacklist' ? '2px solid var(--error)' : '1px solid var(--card-border)',
                      background: selectedListType === 'blacklist' ? 'rgba(239, 68, 68, 0.1)' : 'var(--bg)',
                      color: selectedListType === 'blacklist' ? 'var(--error)' : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontWeight: '600',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <ShieldOff size={18} />
                    Blacklist
                  </button>
                  <button
                    onClick={() => setSelectedListType('unfollow')}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: selectedListType === 'unfollow' ? '2px solid var(--warning)' : '1px solid var(--card-border)',
                      background: selectedListType === 'unfollow' ? 'rgba(245, 158, 11, 0.1)' : 'var(--bg)',
                      color: selectedListType === 'unfollow' ? 'var(--warning)' : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontWeight: '600',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <UserMinus size={18} />
                    Unfollow
                  </button>
                </div>

                <div style={{ 
                  fontSize: '13px', 
                  color: 'var(--text-secondary)',
                  padding: '12px',
                  background: 'var(--surface)',
                  borderRadius: '6px',
                  borderLeft: '3px solid var(--primary)'
                }}>
                  {selectedListType === 'whitelist' ? (
                    <span>🛡️ <strong>Whitelist :</strong> Les comptes dans cette liste ne seront JAMAIS unfollowed</span>
                  ) : selectedListType === 'blacklist' ? (
                    <span>🚫 <strong>Blacklist :</strong> Les comptes dans cette liste ne seront JAMAIS interagis (likes, follows, etc.)</span>
                  ) : (
                    <span>👥 <strong>Unfollow :</strong> Les comptes dans cette liste seront unfollowed lors du prochain script unfollow</span>
                  )}
                </div>
              </div>

              {/* Affichage de la liste sélectionnée */}
              {accountLists[selectedListType]?.exists ? (
                <div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--btn-text)' }}>
                      {accountLists[selectedListType].count} username(s)
                    </div>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Supprimer complètement la ${selectedListType} ?`)) return;
                        try {
                          await axios.delete(`/api/account-lists/${selectedListAccount.username}/delete`, {
                            data: { listType: selectedListType }
                          });
                          const response = await axios.get(`/api/account-lists/${selectedListAccount.username}`);
                          setAccountLists(response.data.lists);
                          alert(`✅ ${selectedListType} supprimée`);
                        } catch (error) {
                          alert(`❌ Erreur: ${error.response?.data?.error || error.message}`);
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--error)',
                        background: 'rgba(239, 68, 68, 0.1)',
                        color: 'var(--error)',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}
                    >
                      <Trash2 size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                      Supprimer la liste
                    </button>
                  </div>

                  <div style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto',
                    background: 'var(--bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    {accountLists[selectedListType].entries.map((username, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          background: 'var(--card-bg)',
                          borderRadius: '6px',
                          marginBottom: '6px'
                        }}
                      >
                        <span style={{ color: 'var(--btn-text)', fontSize: '14px' }}>@{username}</span>
                        <button
                          onClick={async () => {
                            try {
                              await axios.delete(`/api/account-lists/${selectedListAccount.username}/remove`, {
                                data: { listType: selectedListType, usernames: [username] }
                              });
                              const response = await axios.get(`/api/account-lists/${selectedListAccount.username}`);
                              setAccountLists(response.data.lists);
                            } catch (error) {
                              alert(`❌ Erreur: ${error.response?.data?.error || error.message}`);
                            }
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: 'none',
                            background: 'rgba(239, 68, 68, 0.1)',
                            color: 'var(--error)',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Ajouter un username */}
                  <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      placeholder="Ajouter un @username"
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--card-border)',
                        background: 'var(--bg)',
                        color: 'var(--btn-text)',
                        fontSize: '14px'
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          document.getElementById('add-username-btn').click();
                        }
                      }}
                    />
                    <button
                      id="add-username-btn"
                      onClick={async () => {
                        if (!newUsername.trim()) return;
                        const username = newUsername.trim().replace('@', '');
                        try {
                          await axios.post(`/api/account-lists/${selectedListAccount.username}/add`, {
                            listType: selectedListType,
                            usernames: [username]
                          });
                          const response = await axios.get(`/api/account-lists/${selectedListAccount.username}`);
                          setAccountLists(response.data.lists);
                          setNewUsername('');
                        } catch (error) {
                          alert(`❌ Erreur: ${error.response?.data?.error || error.message}`);
                        }
                      }}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '6px',
                        border: 'none',
                        background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                        color: 'var(--btn-text)',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}
                    >
                      <Plus size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                      Ajouter
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ 
                    fontSize: '48px', 
                    marginBottom: '16px',
                    opacity: 0.3
                  }}>
                    {selectedListType === 'whitelist' ? '🛡️' : selectedListType === 'blacklist' ? '🚫' : '👥'}
                  </div>
                  <p style={{ fontSize: '16px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
                    Aucune {selectedListType} pour ce compte
                  </p>
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`/api/account-lists/${selectedListAccount.username}/create`, {
                          listType: selectedListType
                        });
                        const response = await axios.get(`/api/account-lists/${selectedListAccount.username}`);
                        setAccountLists(response.data.lists);
                        alert(`✅ ${selectedListType} créée avec succès`);
                      } catch (error) {
                        alert(`❌ Erreur: ${error.response?.data?.error || error.message}`);
                      }
                    }}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                      color: 'var(--btn-text)',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}
                  >
                    <Plus size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Créer la {selectedListType}
                  </button>
                </div>
              )}
            </ModalBody>
            
            <ModalFooter>
              <CancelButton onClick={() => setShowListsModal(false)}>
                Fermer
              </CancelButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Modale de configuration du prompt Gemini */}
      {showGeminiPromptModal && selectedGeminiAccount && (
        <ModalOverlay onClick={() => setShowGeminiPromptModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <ModalHeader>
              <ModalTitle>
                <span style={{ fontSize: '28px' }}>🤖</span>
                {`Prompt Gemini - @${selectedGeminiAccount.username}`}
              </ModalTitle>
              <CloseButton onClick={() => setShowGeminiPromptModal(false)}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ 
                  padding: '16px',
                  background: 'var(--surface)',
                  borderRadius: '12px',
                  borderLeft: '4px solid var(--primary)',
                  marginBottom: '20px'
                }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary)', marginBottom: '8px' }}>
                    A propos de l'analyse Gemini
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    Ce prompt sera utilise par Gemini pour analyser chaque profil visite lors des interactions.
                    Definissez vos criteres d evaluation pour filtrer intelligemment les profils pertinents.
                  </div>
                </div>

                {/* Affichage du prompt actuel */}
                <div style={{ 
                  padding: '16px',
                  background: 'rgba(34, 139, 230, 0.1)',
                  borderRadius: '12px',
                  borderLeft: '4px solid #228BE6',
                  marginBottom: '20px'
                }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#228BE6', marginBottom: '8px' }}>
                    📋 Prompt Gemini actuel
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: 'var(--text-secondary)', 
                    lineHeight: '1.6',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    background: 'var(--bg)',
                    padding: '12px',
                    borderRadius: '8px',
                    marginTop: '8px'
                  }}>
                    {geminiPrompt || defaultPrompt || 'Aucun prompt défini'}
                  </div>
                </div>

                <label style={{ 
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '700',
                  color: 'var(--btn-text)',
                  marginBottom: '12px'
                }}>
                  Prompt personnalisé
                </label>
                <textarea
                  value={geminiPrompt}
                  onChange={(e) => setGeminiPrompt(e.target.value)}
                  placeholder={defaultPrompt || "Définissez votre prompt personnalisé..."}
                  style={{
                    width: '100%',
                    minHeight: '300px',
                    padding: '16px',
                    background: 'var(--bg)',
                    border: '2px solid var(--card-border)',
                    borderRadius: '12px',
                    color: 'var(--btn-text)',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    lineHeight: '1.6',
                    resize: 'vertical'
                  }}
                />
                <div style={{ fontSize: '12px', color: 'var(--primary)', marginTop: '8px' }}>
                  Le prompt doit retourner un JSON avec: should_interact, confidence, category, reason, interests, engagement_potential
                </div>
              </div>

              {!geminiPrompt && defaultPrompt && (
                <div style={{ 
                  padding: '12px',
                  background: 'rgba(245, 158, 11, 0.1)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: 'var(--warning)',
                  marginTop: '12px'
                }}>
                  Aucun prompt personnalise. Le prompt par defaut sera utilise.
                </div>
              )}
            </ModalBody>
            
            <ModalFooter>
              <CancelButton onClick={() => setShowGeminiPromptModal(false)}>
                Annuler
              </CancelButton>
              {geminiPrompt && (
                <CancelButton 
                  onClick={async () => {
                    if (!window.confirm('Supprimer le prompt personnalise et utiliser le prompt par defaut ?')) return;
                    try {
                      await axios.delete(`/api/gemini-prompts/${selectedGeminiAccount.id}`);
                      setGeminiPrompt('');
                      alert('Prompt supprime avec succes');
                    } catch (error) {
                      alert(`Erreur: ${error.response?.data?.error || error.message}`);
                    }
                  }}
                  style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--error)', borderColor: 'var(--error)' }}
                >
                  Supprimer
                </CancelButton>
              )}
              <SubmitButton
                onClick={async () => {
                  if (!geminiPrompt.trim()) {
                    alert('Le prompt ne peut pas etre vide');
                    return;
                  }
                  try {
                    await axios.post(`/api/gemini-prompts/${selectedGeminiAccount.id}`, {
                      prompt: geminiPrompt
                    });
                    alert('Prompt enregistre avec succes');
                    setShowGeminiPromptModal(false);
                  } catch (error) {
                    alert(`Erreur: ${error.response?.data?.error || error.message}`);
                  }
                }}
              >
                <Settings size={18} />
                Enregistrer
              </SubmitButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Modale de lancement de scripts */}
      {showScriptModal && selectedScriptAccount && (
        <ModalOverlay onClick={() => setShowScriptModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <ModalHeader>
              <ModalTitle>
                <Play size={28} style={{ color: 'var(--primary)' }} />
                Lancer un script - @{selectedScriptAccount.username}
              </ModalTitle>
              <CloseButton onClick={() => setShowScriptModal(false)}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ 
                  background: 'linear-gradient(135deg, var(--surface), rgba(168, 85, 247, 0.1))',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ position: 'relative' }}>
                      <img
                        src={getLocalProfilePicUrl(selectedScriptAccount.username)}
                        alt={selectedScriptAccount.username}
                        style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '50%',
                          border: '3px solid var(--primary)',
                          objectFit: 'cover',
                          boxShadow: 'var(--shadow)'
                        }}
                        onError={(e) => {
                          const step = parseInt(e.target.dataset.fb || '0');
                          if (step === 0) { e.target.dataset.fb = '1'; e.target.src = getProfilePicUrl(selectedScriptAccount.username); }
                          else if (step === 1) { e.target.dataset.fb = '2'; e.target.src = `https://unavatar.io/instagram/${selectedScriptAccount.username}`; }
                          else { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }
                        }}
                      />
                      <div
                        style={{
                          display: 'none',
                          width: '64px',
                          height: '64px',
                          borderRadius: '50%',
                          border: '3px solid var(--primary)',
                          background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          fontWeight: '700',
                          color: 'var(--btn-text)',
                          boxShadow: 'var(--shadow)'
                        }}
                      >
                        {selectedScriptAccount.username.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Instagram size={20} style={{ color: '#E1306C' }} />
                        <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--btn-text)' }}>
                          @{selectedScriptAccount.username}
                        </div>
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                        {selectedScriptAccount.device_name || selectedScriptAccount.device_id}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sélection du type de script */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ 
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '700',
                    color: 'var(--btn-text)',
                    marginBottom: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    Type de script
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                    <button
                      onClick={() => setScriptParams({ ...scriptParams, scriptType: 'interact', interactionType: 'followers' })}
                      style={{
                        padding: '16px',
                        borderRadius: '12px',
                        border: scriptParams.scriptType === 'interact' 
                          ? '2px solid var(--primary)' 
                          : '2px solid var(--card-border)',
                        background: scriptParams.scriptType === 'interact'
                          ? 'var(--surface)'
                          : 'var(--bg)',
                        color: 'var(--btn-text)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: '15px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <Heart size={20} style={{ color: 'var(--accent)' }} />
                      Interact
                    </button>
                    <button
                      onClick={() => setScriptParams({ ...scriptParams, scriptType: 'unfollow' })}
                      style={{
                        padding: '16px',
                        borderRadius: '12px',
                        border: scriptParams.scriptType === 'unfollow' 
                          ? '2px solid var(--primary)' 
                          : '2px solid var(--card-border)',
                        background: scriptParams.scriptType === 'unfollow'
                          ? 'var(--surface)'
                          : 'var(--bg)',
                        color: 'var(--btn-text)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: '15px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <UserMinus size={20} style={{ color: 'var(--error)' }} />
                      Unfollow
                    </button>
                    <button
                      onClick={() => setScriptParams({ ...scriptParams, scriptType: 'follower_analysis' })}
                      style={{
                        padding: '16px',
                        borderRadius: '12px',
                        border: scriptParams.scriptType === 'follower_analysis' 
                          ? '2px solid var(--primary)' 
                          : '2px solid var(--card-border)',
                        background: scriptParams.scriptType === 'follower_analysis'
                          ? 'var(--surface)'
                          : 'var(--bg)',
                        color: 'var(--btn-text)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: '15px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <Users size={20} style={{ color: 'var(--info)' }} />
                      Analysis
                    </button>
                    <button
                      onClick={() => setScriptParams({ ...scriptParams, scriptType: 'scrape' })}
                      style={{
                        padding: '16px',
                        borderRadius: '12px',
                        border: scriptParams.scriptType === 'scrape' 
                          ? '2px solid var(--primary)' 
                          : '2px solid var(--card-border)',
                        background: scriptParams.scriptType === 'scrape'
                          ? 'var(--surface)'
                          : 'var(--bg)',
                        color: 'var(--btn-text)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: '15px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <Users size={20} style={{ color: 'var(--warning)' }} />
                      Scrape
                    </button>
                  </div>
                </div>

                {/* Paramètres pour Interact */}
                {scriptParams.scriptType === 'interact' && (
                  <div style={{ 
                    background: 'var(--bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '12px',
                    padding: '20px'
                  }}>
                    {/* Type d'interaction */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ 
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '700',
                        color: 'var(--btn-text)',
                        marginBottom: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}>
                        Type d'interaction
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <button
                          onClick={() => setScriptParams({ ...scriptParams, interactionType: 'followers' })}
                          style={{
                            padding: '14px',
                            borderRadius: '10px',
                            border: scriptParams.interactionType === 'followers' 
                              ? '2px solid var(--primary)' 
                              : '2px solid var(--card-border)',
                            background: scriptParams.interactionType === 'followers'
                              ? 'var(--surface)'
                              : 'var(--card-bg)',
                            color: 'var(--btn-text)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <Users size={18} style={{ color: 'var(--accent)' }} />
                          Followers
                        </button>
                        <button
                          onClick={() => setScriptParams({ ...scriptParams, interactionType: 'following' })}
                          style={{
                            padding: '14px',
                            borderRadius: '10px',
                            border: scriptParams.interactionType === 'following' 
                              ? '2px solid var(--primary)' 
                              : '2px solid var(--card-border)',
                            background: scriptParams.interactionType === 'following'
                              ? 'var(--surface)'
                              : 'var(--card-bg)',
                            color: 'var(--btn-text)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <Users size={18} style={{ color: 'var(--secondary)' }} />
                          Following
                        </button>
                        <button
                          onClick={() => setScriptParams({ ...scriptParams, interactionType: 'feed' })}
                          style={{
                            padding: '14px',
                            borderRadius: '10px',
                            border: scriptParams.interactionType === 'feed' 
                              ? '2px solid var(--primary)' 
                              : '2px solid var(--card-border)',
                            background: scriptParams.interactionType === 'feed'
                              ? 'var(--surface)'
                              : 'var(--card-bg)',
                            color: 'var(--btn-text)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <Heart size={18} style={{ color: 'var(--success)' }} />
                          Feed
                        </button>
                        <button
                          onClick={() => setScriptParams({ ...scriptParams, interactionType: 'stories' })}
                          style={{
                            padding: '14px',
                            borderRadius: '10px',
                            border: scriptParams.interactionType === 'stories' 
                              ? '2px solid var(--primary)' 
                              : '2px solid var(--card-border)',
                            background: scriptParams.interactionType === 'stories'
                              ? 'var(--surface)'
                              : 'var(--card-bg)',
                            color: 'var(--btn-text)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <Heart size={18} style={{ color: 'var(--warning)' }} />
                          Stories
                        </button>
                        <button
                          onClick={() => setScriptParams({ ...scriptParams, interactionType: 'post-likers' })}
                          style={{
                            padding: '14px',
                            borderRadius: '10px',
                            border: scriptParams.interactionType === 'post-likers' 
                              ? '2px solid var(--primary)' 
                              : '2px solid var(--card-border)',
                            background: scriptParams.interactionType === 'post-likers'
                              ? 'var(--surface)'
                              : 'var(--card-bg)',
                            color: 'var(--btn-text)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <Heart size={18} style={{ color: 'var(--accent)' }} />
                          Post Likers
                        </button>
                        <button
                          onClick={() => setScriptParams({ ...scriptParams, interactionType: 'hashtag' })}
                          style={{
                            padding: '14px',
                            borderRadius: '10px',
                            border: scriptParams.interactionType === 'hashtag' 
                              ? '2px solid var(--primary)' 
                              : '2px solid var(--card-border)',
                            background: scriptParams.interactionType === 'hashtag'
                              ? 'var(--surface)'
                              : 'var(--card-bg)',
                            color: 'var(--btn-text)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <Hash size={18} style={{ color: 'var(--info)' }} />
                          Hashtag
                        </button>
                      </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ 
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--text-secondary)',
                        marginBottom: '8px'
                      }}>
                        Cible spécifique (optionnel)
                      </label>
                      <input
                        type="text"
                        placeholder="Laisser vide pour utiliser les cibles du compte"
                        value={scriptParams.specificTarget}
                        onChange={(e) => setScriptParams({ ...scriptParams, specificTarget: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: 'var(--card-bg)',
                          border: '1px solid var(--card-border)',
                          borderRadius: '8px',
                          color: 'var(--btn-text)',
                          fontSize: '14px'
                        }}
                      />
                      <div style={{ fontSize: '12px', color: 'var(--primary)', marginTop: '6px' }}>
                        Exemple: @username ou #hashtag
                      </div>
                    </div>

                    {/* Affichage des cibles du compte */}
                    {accountTargets.length > 0 && (
                      <div style={{ 
                        marginBottom: '16px',
                        padding: '16px',
                        background: 'var(--card-bg)',
                        border: '1px solid var(--card-border)',
                        borderRadius: '12px'
                      }}>
                        <div style={{ 
                          fontSize: '13px',
                          fontWeight: '700',
                          color: 'var(--btn-text)',
                          marginBottom: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Users size={16} style={{ color: 'var(--primary)' }} />
                          Cibles associées au compte ({accountTargets.length})
                        </div>
                        <div style={{ 
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px'
                        }}>
                          {accountTargets.map((target, index) => (
                            <div
                              key={index}
                              style={{
                                padding: '6px 12px',
                                background: 'var(--surface)',
                                border: '1px solid var(--border)',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                color: 'var(--btn-text)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              {target.type === 'user' ? (
                                <Users size={14} style={{ color: 'var(--accent)' }} />
                              ) : (
                                <Hash size={14} style={{ color: 'var(--info)' }} />
                              )}
                              {target.type === 'user' ? '@' : '#'}{target.value}
                              {target.target_weight > 1 && (
                                <span style={{ 
                                  fontSize: '10px',
                                  color: 'var(--primary)',
                                  marginLeft: '4px'
                                }}>
                                  ×{target.target_weight}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Option d'analyse Gemini */}
                    <div style={{ 
                      marginTop: '20px',
                      padding: '16px',
                      background: 'var(--surface)',
                      border: '2px solid var(--border)',
                      borderRadius: '12px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontSize: '14px',
                            fontWeight: '700',
                            color: 'var(--btn-text)',
                            marginBottom: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <Sparkles size={18} style={{ color: 'var(--primary)' }} />
                            Analyse de profil par Gemini
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                            Screenshot et analyse IA de chaque profil visité pour déterminer la pertinence de l'interaction
                          </div>
                        </div>
                        <label style={{ 
                          position: 'relative',
                          display: 'inline-block',
                          width: '56px',
                          height: '30px',
                          marginLeft: '16px',
                          flexShrink: 0
                        }}>
                          <input
                            type="checkbox"
                            checked={scriptParams.geminiAnalysis}
                            onChange={(e) => setScriptParams({ ...scriptParams, geminiAnalysis: e.target.checked })}
                            style={{ opacity: 0, width: 0, height: 0 }}
                          />
                          <span style={{
                            position: 'absolute',
                            cursor: 'pointer',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: scriptParams.geminiAnalysis ? 'var(--primary)' : 'var(--card-border)',
                            transition: '0.3s',
                            borderRadius: '30px',
                            boxShadow: scriptParams.geminiAnalysis ? '0 0 12px var(--primary)' : 'none'
                          }}>
                            <span style={{
                              position: 'absolute',
                              content: '',
                              height: '22px',
                              width: '22px',
                              left: scriptParams.geminiAnalysis ? '30px' : '4px',
                              bottom: '4px',
                              background: 'white',
                              transition: '0.3s',
                              borderRadius: '50%'
                            }} />
                          </span>
                        </label>
                      </div>
                      {scriptParams.geminiAnalysis && (
                        <>
                          <div style={{ 
                            marginTop: '12px',
                            padding: '12px',
                            background: 'var(--surface)',
                            borderRadius: '8px',
                            fontSize: '12px',
                            color: 'var(--primary)',
                            lineHeight: '1.6'
                          }}>
                            <Sparkles size={14} style={{ display: 'inline', marginRight: '4px' }} /> <strong>Fonctionnalités activées :</strong><br/>
                            • Screenshot automatique de chaque profil<br/>
                            • Analyse IA avec prompt personnalisé<br/>
                            • Enrichissement de la table profils visités<br/>
                            • Filtrage intelligent des interactions
                          </div>
                          <div style={{ 
                            marginTop: '16px',
                            padding: '16px',
                            background: 'var(--bg)',
                            border: '2px solid var(--border)',
                            borderRadius: '12px'
                          }}>
                            <div style={{ 
                              fontSize: '14px',
                              fontWeight: '700',
                              color: 'var(--btn-text)',
                              marginBottom: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <Settings size={16} style={{ color: 'var(--primary)' }} />
                              Prompt Gemini personnalisé
                            </div>
                            <textarea
                              value={geminiPrompt}
                              onChange={(e) => setGeminiPrompt(e.target.value)}
                              placeholder={defaultPrompt || "Définissez votre prompt personnalisé pour l'analyse Gemini..."}
                              onFocus={async () => {
                                // Charger le prompt au focus si pas encore chargé
                                if (!geminiPrompt && !defaultPrompt && selectedScriptAccount) {
                                  try {
                                    const response = await axios.get(`/api/gemini-prompts/${selectedScriptAccount.id}`);
                                    if (response.data.prompt) {
                                      setGeminiPrompt(response.data.prompt.prompt);
                                    } else {
                                      setDefaultPrompt(response.data.default_prompt || '');
                                    }
                                  } catch (error) {
                                    console.error('Erreur chargement prompt:', error);
                                  }
                                }
                              }}
                              style={{
                                width: '100%',
                                minHeight: '120px',
                                padding: '12px',
                                background: 'var(--card-bg)',
                                border: '2px solid var(--card-border)',
                                borderRadius: '8px',
                                color: 'var(--btn-text)',
                                fontSize: '13px',
                                fontFamily: 'monospace',
                                lineHeight: '1.5',
                                resize: 'vertical'
                              }}
                            />
                            <div style={{ 
                              fontSize: '11px', 
                              color: 'var(--primary)', 
                              marginTop: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}>
                              <span>Le prompt doit retourner un JSON avec: should_interact, confidence, category, etc.</span>
                              {geminiPrompt && (
                                <button
                                  onClick={async () => {
                                    if (!window.confirm('Supprimer le prompt personnalisé et utiliser le prompt par défaut ?')) return;
                                    try {
                                      await axios.delete(`/api/gemini-prompts/${selectedScriptAccount.id}`);
                                      setGeminiPrompt('');
                                      alert('✅ Prompt supprimé');
                                    } catch (error) {
                                      alert(`❌ Erreur: ${error.message}`);
                                    }
                                  }}
                                  style={{
                                    padding: '4px 8px',
                                    background: 'transparent',
                                    border: '1px solid var(--error)',
                                    borderRadius: '4px',
                                    color: 'var(--error)',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  Réinitialiser
                                </button>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Paramètres pour Unfollow */}
                {scriptParams.scriptType === 'unfollow' && (
                  <div style={{ 
                    background: 'var(--bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '12px',
                    padding: '20px'
                  }}>
                    {/* Type de script Unfollow */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ 
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '700',
                        color: 'var(--btn-text)',
                        marginBottom: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}>
                        Type de script Unfollow
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                        <button
                          onClick={() => setScriptParams({ ...scriptParams, unfollowType: 'database-global-search' })}
                          style={{
                            padding: '12px 8px',
                            borderRadius: '8px',
                            border: scriptParams.unfollowType === 'database-global-search' 
                              ? '2px solid var(--primary)' 
                              : '2px solid var(--card-border)',
                            background: scriptParams.unfollowType === 'database-global-search'
                              ? 'var(--surface)'
                              : 'var(--card-bg)',
                            color: 'var(--btn-text)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontSize: '12px',
                            fontWeight: '600',
                            textAlign: 'center'
                          }}
                        >
                          ⚡ Database Global Search
                        </button>
                        <button
                          onClick={() => setScriptParams({ ...scriptParams, unfollowType: 'postgres' })}
                          style={{
                            padding: '12px 8px',
                            borderRadius: '8px',
                            border: scriptParams.unfollowType === 'postgres' 
                              ? '2px solid var(--primary)' 
                              : '2px solid var(--card-border)',
                            background: scriptParams.unfollowType === 'postgres'
                              ? 'var(--surface)'
                              : 'var(--card-bg)',
                            color: 'var(--btn-text)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontSize: '12px',
                            fontWeight: '600',
                            textAlign: 'center'
                          }}
                        >
                          PostgreSQL
                        </button>
                        <button
                          onClick={() => setScriptParams({ ...scriptParams, unfollowType: 'simple' })}
                          style={{
                            padding: '12px 8px',
                            borderRadius: '8px',
                            border: scriptParams.unfollowType === 'simple' 
                              ? '2px solid var(--primary)' 
                              : '2px solid var(--card-border)',
                            background: scriptParams.unfollowType === 'simple'
                              ? 'var(--surface)'
                              : 'var(--card-bg)',
                            color: 'var(--btn-text)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontSize: '12px',
                            fontWeight: '600',
                            textAlign: 'center'
                          }}
                        >
                          Simple
                        </button>
                        <button
                          onClick={() => setScriptParams({ ...scriptParams, unfollowType: 'direct' })}
                          style={{
                            padding: '12px 8px',
                            borderRadius: '8px',
                            border: scriptParams.unfollowType === 'direct' 
                              ? '2px solid var(--primary)' 
                              : '2px solid var(--card-border)',
                            background: scriptParams.unfollowType === 'direct'
                              ? 'var(--surface)'
                              : 'var(--card-bg)',
                            color: 'var(--btn-text)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontSize: '12px',
                            fontWeight: '600',
                            textAlign: 'center'
                          }}
                        >
                          Direct
                        </button>
                        <button
                          onClick={() => setScriptParams({ ...scriptParams, unfollowType: 'standard' })}
                          style={{
                            padding: '12px 8px',
                            borderRadius: '8px',
                            border: scriptParams.unfollowType === 'standard' 
                              ? '2px solid var(--primary)' 
                              : '2px solid var(--card-border)',
                            background: scriptParams.unfollowType === 'standard'
                              ? 'var(--surface)'
                              : 'var(--card-bg)',
                            color: 'var(--btn-text)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontSize: '12px',
                            fontWeight: '600',
                            textAlign: 'center'
                          }}
                        >
                          Standard
                        </button>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--primary)', marginTop: '8px' }}>
                        {scriptParams.unfollowType === 'database-global-search' && '⚡ Ultra rapide - Recherche directe dans Instagram'}
                        {scriptParams.unfollowType === 'postgres' && 'Moyenne - Utilise le fichier PostgreSQL'}
                        {scriptParams.unfollowType === 'simple' && 'Moyenne - Utilise le fichier simple'}
                        {scriptParams.unfollowType === 'direct' && 'Moyenne - Utilise le fichier direct'}
                        {scriptParams.unfollowType === 'standard' && 'Lente - Mode classique QLICKR'}
                      </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ 
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--text-secondary)',
                        marginBottom: '8px'
                      }}>
                        Nombre d'unfollow
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="1000"
                        value={scriptParams.unfollowCount}
                        onChange={(e) => setScriptParams({ ...scriptParams, unfollowCount: parseInt(e.target.value) || 50 })}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: 'var(--card-bg)',
                          border: '1px solid var(--card-border)',
                          borderRadius: '8px',
                          color: 'var(--btn-text)',
                          fontSize: '14px'
                        }}
                      />
                      <div style={{ fontSize: '12px', color: 'var(--primary)', marginTop: '6px' }}>
                        Nombre de comptes à unfollow: {scriptParams.unfollowCount}
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ 
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--text-secondary)',
                        marginBottom: '8px'
                      }}>
                        Durée maximale (minutes)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="120"
                        value={scriptParams.unfollowMaxDuration}
                        onChange={(e) => setScriptParams({ ...scriptParams, unfollowMaxDuration: parseInt(e.target.value) || 30 })}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: 'var(--card-bg)',
                          border: '1px solid var(--card-border)',
                          borderRadius: '8px',
                          color: 'var(--btn-text)',
                          fontSize: '14px'
                        }}
                      />
                      <div style={{ fontSize: '12px', color: 'var(--primary)', marginTop: '6px' }}>
                        Le script s'arrêtera automatiquement après {scriptParams.unfollowMaxDuration} minutes
                      </div>
                    </div>
                  </div>
                )}

                {/* Paramètres pour Follower Analysis */}
                {scriptParams.scriptType === 'follower_analysis' && (
                  <div style={{ 
                    background: 'var(--bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '12px',
                    padding: '20px',
                    textAlign: 'center'
                  }}>
                    <Users size={48} style={{ color: 'var(--info)', margin: '0 auto 16px' }} />
                    <div style={{ fontSize: '15px', color: 'var(--btn-text)', marginBottom: '8px', fontWeight: '600' }}>
                      Analyse des followers
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                      Ce script va analyser les followers du compte et générer un rapport détaillé.
                    </div>
                  </div>
                )}

                {/* Paramètres pour Scrape */}
                {scriptParams.scriptType === 'scrape' && (
                  <div style={{ 
                    background: 'var(--bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '12px',
                    padding: '20px'
                  }}>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ 
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--text-secondary)',
                        marginBottom: '8px'
                      }}>
                        Cible à scraper (sans @)
                      </label>
                      <input
                        type="text"
                        placeholder="Ex: natgeo"
                        value={scriptParams.scrapeTarget}
                        onChange={(e) => setScriptParams({ ...scriptParams, scrapeTarget: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: 'var(--card-bg)',
                          border: '1px solid var(--card-border)',
                          borderRadius: '8px',
                          color: 'var(--btn-text)',
                          fontSize: '14px'
                        }}
                      />
                      <div style={{ fontSize: '12px', color: 'var(--primary)', marginTop: '6px' }}>
                        Entrez le nom d'utilisateur de la cible à scraper
                      </div>
                    </div>
                    {/* Limites de scrape */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                          Limite totale (range)
                        </label>
                        <input
                          type="text"
                          placeholder="200-300"
                          value={scriptParams.totalScrapeLimit}
                          onChange={(e) => setScriptParams({ ...scriptParams, totalScrapeLimit: e.target.value })}
                          style={{ width: '100%', padding: '10px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--btn-text)', fontSize: '13px' }}
                        />
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>Ex: 200-300 (vide = valeur DB)</div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                          Limite par source (range)
                        </label>
                        <input
                          type="text"
                          placeholder="200-300"
                          value={scriptParams.scrapeLimitPerSource}
                          onChange={(e) => setScriptParams({ ...scriptParams, scrapeLimitPerSource: e.target.value })}
                          style={{ width: '100%', padding: '10px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--btn-text)', fontSize: '13px' }}
                        />
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>Ex: 50-100 (vide = valeur DB)</div>
                      </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ 
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '700',
                        color: 'var(--btn-text)',
                        marginBottom: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}>
                        Cibles de scrapping
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        {[
                          { key: 'followers', label: 'Followers', icon: <Users size={16} />, color: 'var(--accent)' },
                          { key: 'following', label: 'Following', icon: <Users size={16} />, color: 'var(--secondary)' },
                          { key: 'feed', label: 'Feed', icon: <Heart size={16} />, color: 'var(--success)' },
                          { key: 'stories', label: 'Stories', icon: <Heart size={16} />, color: 'var(--warning)' },
                          { key: 'post-likers', label: 'Post Likers', icon: <Heart size={16} />, color: 'var(--accent)' },
                          { key: 'hashtag', label: 'Hashtag', icon: <Hash size={16} />, color: 'var(--info)' },
                        ].map(t => {
                          const checked = (scriptParams.scrapeTypes || []).includes(t.key);
                          return (
                            <label key={t.key} style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '10px 12px', borderRadius: '10px', cursor: 'pointer',
                              border: checked ? '2px solid var(--primary)' : '2px solid var(--card-border)',
                              background: checked ? 'var(--surface)' : 'var(--card-bg)',
                              transition: 'all 0.2s ease', fontSize: '13px', fontWeight: '600', color: 'var(--btn-text)',
                            }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  const types = scriptParams.scrapeTypes || [];
                                  const next = checked ? types.filter(x => x !== t.key) : [...types, t.key];
                                  setScriptParams({ ...scriptParams, scrapeTypes: next, scrapeType: next[0] || 'followers' });
                                }}
                                style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }}
                              />
                              <span style={{ color: t.color, display: 'flex', alignItems: 'center' }}>{t.icon}</span>
                              {t.label}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Option d'analyse Gemini pour Scrape */}
                    <div style={{ 
                      marginTop: '20px',
                      padding: '16px',
                      background: 'var(--surface)',
                      border: '2px solid var(--border)',
                      borderRadius: '12px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontSize: '14px',
                            fontWeight: '700',
                            color: 'var(--btn-text)',
                            marginBottom: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <Sparkles size={18} style={{ color: 'var(--primary)' }} />
                            Analyse de profil par Gemini
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                            Screenshot et analyse IA de chaque profil scrapé pour enrichir les données
                          </div>
                        </div>
                        <label style={{ 
                          position: 'relative',
                          display: 'inline-block',
                          width: '56px',
                          height: '30px',
                          marginLeft: '16px',
                          flexShrink: 0
                        }}>
                          <input
                            type="checkbox"
                            checked={scriptParams.geminiAnalysis}
                            onChange={(e) => setScriptParams({ ...scriptParams, geminiAnalysis: e.target.checked })}
                            style={{ opacity: 0, width: 0, height: 0 }}
                          />
                          <span style={{
                            position: 'absolute',
                            cursor: 'pointer',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: scriptParams.geminiAnalysis ? 'var(--primary)' : 'var(--card-border)',
                            transition: '0.3s',
                            borderRadius: '30px',
                            boxShadow: scriptParams.geminiAnalysis ? '0 0 12px var(--primary)' : 'none'
                          }}>
                            <span style={{
                              position: 'absolute',
                              content: '""',
                              height: '22px',
                              width: '22px',
                              left: scriptParams.geminiAnalysis ? '30px' : '4px',
                              bottom: '4px',
                              background: 'white',
                              transition: '0.3s',
                              borderRadius: '50%'
                            }} />
                          </span>
                        </label>
                      </div>
                      {scriptParams.geminiAnalysis && (
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('🔍 Bouton prompt cliqué (Scrape)');
                            console.log('selectedScriptAccount:', selectedScriptAccount);
                            
                            if (!selectedScriptAccount || !selectedScriptAccount.id) {
                              console.error('❌ selectedScriptAccount est null ou sans ID');
                              alert('❌ Erreur: Compte non sélectionné');
                              return;
                            }
                            
                            setSelectedGeminiAccount(selectedScriptAccount);
                            try {
                              console.log(`📡 Appel API: /api/gemini-prompts/${selectedScriptAccount.id}`);
                              const response = await axios.get(`/api/gemini-prompts/${selectedScriptAccount.id}`);
                              console.log('✅ Réponse API:', response.data);
                              if (response.data.prompt) {
                                setGeminiPrompt(response.data.prompt.prompt);
                              } else {
                                setGeminiPrompt('');
                                setDefaultPrompt(response.data.default_prompt || '');
                              }
                              setShowGeminiPromptModal(true);
                              console.log('✅ Modale ouverte');
                            } catch (error) {
                              console.error('❌ Erreur récupération prompt:', error);
                              alert(`❌ Erreur: ${error.response?.data?.error || error.message}`);
                            }
                          }}
                          style={{
                            marginTop: '12px',
                            width: '100%',
                            padding: '12px',
                            background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                            border: 'none',
                            borderRadius: '8px',
                            color: 'var(--btn-text)',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <Settings size={16} />
                          Voir / Éditer le prompt personnalisé
                        </button>
                      )}
                    </div>

                    {/* Option Analyse approfondie */}
                    <div style={{ 
                      marginTop: '12px',
                      padding: '14px',
                      background: 'var(--surface)',
                      border: '2px solid var(--border)',
                      borderRadius: '12px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--btn-text)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Search size={18} style={{ color: 'var(--secondary)' }} />
                            Analyse Gemini approfondie
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                            Analyse aussi les followings des profils scrapés pour un enrichissement maximal
                          </div>
                        </div>
                        <label style={{ position: 'relative', display: 'inline-block', width: '56px', height: '30px', marginLeft: '16px', flexShrink: 0 }}>
                          <input
                            type="checkbox"
                            checked={scriptParams.deepAnalysis}
                            onChange={(e) => setScriptParams({ ...scriptParams, deepAnalysis: e.target.checked })}
                            style={{ opacity: 0, width: 0, height: 0 }}
                          />
                          <span style={{
                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                            background: scriptParams.deepAnalysis ? 'var(--secondary)' : 'var(--card-border)',
                            transition: '0.3s', borderRadius: '30px',
                            boxShadow: scriptParams.deepAnalysis ? '0 0 12px var(--secondary)' : 'none'
                          }}>
                            <span style={{
                              position: 'absolute', height: '22px', width: '22px',
                              left: scriptParams.deepAnalysis ? '30px' : '4px', bottom: '4px',
                              background: 'white', transition: '0.3s', borderRadius: '50%'
                            }} />
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ModalBody>

            <ModalFooter>
              <CancelButton onClick={() => setShowScriptModal(false)}>
                Annuler
              </CancelButton>
              <SubmitButton
                onClick={async () => {
                  const account = selectedScriptAccount;
                  
                  // Sauvegarder le prompt Gemini si modifié et analyse activée
                  if (scriptParams.geminiAnalysis && geminiPrompt && account) {
                    try {
                      await axios.post(`/api/gemini-prompts/${account.id}`, {
                        prompt: geminiPrompt
                      });
                    } catch (promptError) {
                      console.error('Erreur sauvegarde prompt:', promptError);
                      // Continue quand même le lancement du script
                    }
                  }
                  
                  if (scriptParams.scriptType === 'scrape') {
                    if (!scriptParams.scrapeTarget) {
                      alert('❌ Veuillez entrer une cible à scraper');
                      return;
                    }
                    const selectedTypes = scriptParams.scrapeTypes || ['followers'];
                    if (selectedTypes.length === 0) {
                      alert('❌ Veuillez sélectionner au moins une cible de scrapping');
                      return;
                    }
                    const activeScrapeType = selectedTypes[0];
                    
                    // Save limits to instagram_device_configs if provided
                    if (scriptParams.totalScrapeLimit || scriptParams.scrapeLimitPerSource) {
                      try {
                        await axios.post(`/api/account-device-settings/${account.id}`, {
                          total_scrape_limit: scriptParams.totalScrapeLimit || undefined,
                          scrape_limit_per_source: scriptParams.scrapeLimitPerSource || undefined,
                        });
                      } catch (_) { /* continue anyway */ }
                    }
                    
                    if (!window.confirm(`Lancer le script SCRAPE pour @${account.username}?\nCible: @${scriptParams.scrapeTarget}\nTypes: ${selectedTypes.join(', ')}\nGemini: ${scriptParams.geminiAnalysis ? 'Oui' : 'Non'}\nAnalyse approfondie: ${scriptParams.deepAnalysis ? 'Oui' : 'Non'}`)) {
                      return;
                    }
                    
                    try {
                      const response = await axios.post(`/api/scrape/start-scrape/${account.id}`, {
                        target_username: scriptParams.scrapeTarget,
                        scrape_type: activeScrapeType,
                        gemini_analysis: scriptParams.geminiAnalysis,
                        deep_analysis: scriptParams.deepAnalysis,
                        total_scrape_limit: scriptParams.totalScrapeLimit || undefined,
                        scrape_limit_per_source: scriptParams.scrapeLimitPerSource || undefined,
                      });
                      
                      if (response.data.success) {
                        alert(`✅ Scrape lancé pour @${scriptParams.scrapeTarget} (${activeScrapeType})\n🤖 Gemini: ${response.data.gemini_enabled ? 'Activé' : 'Désactivé'}\n🔬 Approfondie: ${scriptParams.deepAnalysis ? 'Oui' : 'Non'}`);
                        fetchScriptStatuses();
                        setShowScriptModal(false);
                      } else {
                        alert(`❌ Erreur: ${response.data.error}`);
                      }
                    } catch (error) {
                      console.error('Erreur:', error);
                      alert(`❌ Erreur: ${error.response?.data?.error || error.message}`);
                    }
                  } else if (scriptParams.scriptType === 'interact') {
                    const targetInfo = scriptParams.specificTarget 
                      ? `\nCible: ${scriptParams.specificTarget}` 
                      : '';
                    const interactTypeText = scriptParams.interactType === 'followers' ? 'Followers' : 'Following';
                    
                    if (!window.confirm(`Lancer le script INTERACT pour @${account.username}?${targetInfo}\nType: ${interactTypeText}`)) {
                      return;
                    }
                    
                    try {
                      const requestData = {
                        device_id: account.device_id,
                        username: account.username,
                        card_id: account.id,
                        interact_type: scriptParams.interactType,
                        gemini_analysis: scriptParams.geminiAnalysis
                      };
                      
                      // Ajouter la cible spécifique si définie
                      if (scriptParams.specificTarget && scriptParams.specificTarget.trim()) {
                        requestData.specific_target = scriptParams.specificTarget.trim();
                      }
                      
                      const response = await axios.post('/api/run', requestData);
                      
                      if (response.data.success) {
                        const targetMsg = scriptParams.specificTarget 
                          ? `\nCible: ${scriptParams.specificTarget}` 
                          : `\n${response.data.active_targets || ''} cible(s) active(s)`;
                        alert(`✅ Script INTERACT lancé pour @${account.username}!${targetMsg}`);
                        fetchScriptStatuses();
                        setShowScriptModal(false);
                      } else {
                        alert(`❌ Erreur: ${response.data.error}`);
                      }
                    } catch (error) {
                      console.error('Erreur lancement script:', error);
                      alert(`❌ Erreur: ${error.response?.data?.error || error.message}`);
                    }
                  } else if (scriptParams.scriptType === 'unfollow') {
                    if (!window.confirm(`Lancer le script UNFOLLOW pour @${account.username}?\nType: ${scriptParams.unfollowType}\nNombre: ${scriptParams.unfollowCount}\nDurée max: ${scriptParams.unfollowMaxDuration} minutes`)) {
                      return;
                    }
                    
                    try {
                      const response = await axios.post('/api/unfollow', {
                        account_id: account.id,
                        unfollow_count: scriptParams.unfollowCount,
                        unfollow_max_duration: scriptParams.unfollowMaxDuration,
                        unfollow_type: scriptParams.unfollowType
                      });
                      
                      if (response.data.success) {
                        alert(`✅ Script UNFOLLOW lancé pour @${account.username}!\nType: ${scriptParams.unfollowType}\nNombre: ${scriptParams.unfollowCount}`);
                        fetchScriptStatuses();
                        setShowScriptModal(false);
                      } else {
                        alert(`❌ Erreur: ${response.data.error}`);
                      }
                    } catch (error) {
                      console.error('Erreur lancement script:', error);
                      alert(`❌ Erreur: ${error.response?.data?.error || error.message}`);
                    }
                  } else if (scriptParams.scriptType === 'follower_analysis') {
                    if (!window.confirm(`Lancer l'analyse des followers pour @${account.username}?`)) {
                      return;
                    }
                    
                    try {
                      const response = await axios.post('/api/follower-analysis', {
                        device_id: account.device_id,
                        username: account.username,
                        card_id: account.id
                      });
                      
                      if (response.data.success) {
                        alert(`✅ Analyse des followers lancée pour @${account.username}!`);
                        fetchScriptStatuses();
                        setShowScriptModal(false);
                      } else {
                        alert(`❌ Erreur: ${response.data.error}`);
                      }
                    } catch (error) {
                      console.error('Erreur lancement script:', error);
                      alert(`❌ Erreur: ${error.response?.data?.error || error.message}`);
                    }
                  }
                }}
              >
                <Play size={18} />
                Lancer le script
              </SubmitButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default Accounts;
